// =============================================================================
// SOURCE OF TRUTH for the SynRegis React app.
// `npm run build` compiles this to dist/app.js via esbuild.
// React, ReactDOM and gsap are loaded as globals from CDN by the HTML shell.
// The inline <script type="text/babel"> still present in index.html is LEGACY
// and is stripped at build time — DO NOT edit it; edit this file instead.
// =============================================================================
const { useState, useEffect, useRef } = React;

const useGSAPReveal = (containerRef, delayS = 0.7) => {
  React.useEffect(() => {
    if (!containerRef.current) return;
    const els = containerRef.current.querySelectorAll('.reveal-text');
    if (!els.length || typeof gsap === 'undefined') return;
    // Slow, deliberate rise — runs after the hero image so the image leads, text follows.
    gsap.to(els, {
      y: '0%',
      duration: 2.0,
      ease: 'power3.out',
      stagger: 0.2,
      delay: delayS,
    });
  }, []);
};

const useScrollReveal = (pageRef) => {
  React.useEffect(() => {
    if (!pageRef.current || typeof gsap === 'undefined') return;
    const sections = Array.from(pageRef.current.querySelectorAll('section')).slice(1);
    const entries = [];
    sections.forEach(section => {
      const nonAbs = Array.from(section.children).filter(el =>
        window.getComputedStyle(el).position !== 'absolute'
      );
      nonAbs.forEach(wrapper => {
        const kids = Array.from(wrapper.children).filter(el =>
          window.getComputedStyle(el).position !== 'absolute'
        );
        (kids.length ? kids : [wrapper]).forEach((el, i) => {
          entries.push({ el, delay: i * 0.11 });
        });
      });
    });
    entries.forEach(({ el }) => gsap.set(el, { opacity: 0, y: 28 }));
    // observers declared in outer scope so cleanup can always reach it
    let observers = [];
    const rafId1 = requestAnimationFrame(() => {
      const rafId2 = requestAnimationFrame(() => {
        if (!pageRef.current) return;
        const docHeight = document.documentElement.scrollHeight;
        observers = entries.map(({ el, delay }) => {
          const absBottom = el.getBoundingClientRect().bottom + window.scrollY;
          const nearDocBottom = (docHeight - absBottom) < window.innerHeight * 0.8;
          const margin = nearDocBottom ? '0px 0px -5% 0px' : '0px 0px -30% 0px';
          const obs = new IntersectionObserver(([entry]) => {
            if (!entry.isIntersecting) return;
            gsap.to(el, { opacity: 1, y: 0, duration: 1.8, ease: 'power3.out', delay });
            obs.disconnect();
          }, { threshold: 0.08, rootMargin: margin });
          obs.observe(el);
          return obs;
        });
      });
    });
    return () => {
      cancelAnimationFrame(rafId1);
      observers.forEach(o => o.disconnect());
    };
  }, []);
};

const useImageReveal = (ref, { y = 28, delay = 0.15, duration = 1.9 } = {}) => {
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof gsap === 'undefined') return;
    gsap.set(el, { opacity: 0, y });
    const img = el.tagName === 'IMG' ? el : el.querySelector('img');
    let cleanup = () => {};
    const reveal = () => {
      gsap.to(el, { opacity: 1, y: 0, duration, ease: 'power3.out', delay });
    };
    if (!img || img.complete) {
      reveal();
    } else {
      img.addEventListener('load', reveal, { once: true });
      img.addEventListener('error', reveal, { once: true });
      cleanup = () => {
        img.removeEventListener('load', reveal);
        img.removeEventListener('error', reveal);
      };
    }
    return cleanup;
  }, []);
};

// While the first-load intro runs, hero videos must not start (they would be mid-playback and
// visibly jump when the page is revealed); the intro script starts them on reveal.
const introRunning = () => document.body.classList.contains('intro-active');

const useIsMobile = () => {
  const [mob, setMob] = React.useState(window.innerWidth <= 768);
  React.useEffect(() => {
    const h = () => setMob(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return mob;
};



/* ─── TRANSLATIONS ─── */
const T = {
  fr: {
    nav: { home:'Accueil', services:'Services', about:'À propos', contact:'Contact', faq:'FAQ', cta:'Prendre contact' },
    home: {
      h1a:'Votre réputation',
      h1b:"ne s'arrête pas à la", h1em:'livraison.',
      body:"Dans un marché aussi étroit que l'île Maurice, un immeuble mal géré parle à votre place – longtemps après la remise des clés. SynRegis est le syndic de copropriété qui protège ce que vous avez mis des années à construire.",
      cta1:'Prendre contact', cta2:'Découvrir notre approche',
      badge:'Cabinet de gestion de copropriété premium',
      risk_ol:'Le risque invisible', risk_h2:'Le risque résiduel que personne ne nomme',
      risk_q:"La vente est signée. La livraison est faite. Vous passez au projet suivant. Mais pour l'acquéreur, l'expérience continue.",
      risk_p1:"Et dans une île de 1,3 million d'habitants, cette expérience – bonne ou mauvaise – se raconte.",
      risk_p2a:"L'acquéreur ne fait pas la distinction entre le projet et sa gestion.",
      risk_p2b:"Pour lui, c'est la même expérience. Et cette expérience, c'est votre nom qu'elle porte.",
      risk_stat:"d'habitants à l'île Maurice.", risk_stat2:"Dans un marché où vos prochains acquéreurs connaissent vos anciens, c'est un risque que peu de professionnels calculent vraiment.",
      mkt_ol:'Contexte marché', mkt_h2:"SynRegis est né d'un constat simple.",
      mkt_p1:"À l'île Maurice, il ne manque pas de syndics. Ce qui manque, c'est la compétence juridique pour exercer correctement ce métier.",
      mkt_p2:"Un syndic mal formé en droit de la copropriété ne voit pas venir les problèmes – ",
      mkt_p2b:"il les crée.", mkt_p2c:" Un règlement mal rédigé, un mandat mal structuré, une décision d'AG prise sans base légale : ce sont des litiges en gestation.",
      mkt_p3:"SynRegis a été fondé pour répondre à ce manquement – avec une pratique rigoureuse, une maîtrise du droit et un niveau de service qu'un professionnel de l'immobilier exigeant est en droit d'attendre.",
      prom_ol:'Notre promesse', prom_h2:'Un syndic qui engage sa réputation sur la vôtre',
      prom_body:"SynRegis intervient avant la livraison, structure le cadre de vie dès le premier jour, et gère avec le niveau d'exigence que vos acquéreurs attendent.",
      prom_pillars:[
        {num:'01',title:'Gestion courante rigoureuse',body:'Suivi quotidien des parties communes, équipements et prestataires. Rien ne se règle "quand on y pense".',img:'Images/img_38.jpg'},
        {num:'02',title:'Reporting financier transparent',body:'Comptes lisibles, charges justifiées, budgets prévisionnels maîtrisés. Chaque euro expliqué.',img:'Images/img_39.jpg'},
        {num:'03',title:'AG préparées et tenues',body:"Convocations conformes, PV bilingues, ordre du jour structuré. Une AG bien tenue ne s'entend plus.",img:'Images/img_40.jpg'},
        {num:'04',title:'Communication traçable',body:'Chaque interaction documentée. En cas de litige, vous avez un dossier – pas des souvenirs.',img:'Images/img_41.jpg'},
      ],
      prom_more:'Voir le détail de nos prestations',
      cred:[
        {val:'100+',label:'lots gérés en direct par le fondateur'},
        {val:'Droit',label:'Étude en droit · spécialisation : droit des biens, copropriété & VEFA'},
        {val:'1',label:'seul marché · une seule expertise · construite ici'},
        {val:'High-end',label:'Résidentiel haut de gamme exclusivement'},
      ],
      mem_ol:'La mémoire des résidents',
      mem_h2:'Un acquéreur satisfait est votre meilleure référence pour votre prochain projet.',
      mem_p1:"Un acquéreur insatisfait, lui, parle aussi. Et dans un marché étroit, sa voix porte loin.",
      mem_p2a:"SynRegis gère la copropriété que vous livrez ", mem_p2b:"comme si votre prochaine commercialisation en dépendait.", mem_p2c:" Parce que c'est exactement le cas.",
      cta_h2:"Votre projet mérite une gestion qui préserve ce qu'il vaut.",
      cta_body:"Parlez-nous de votre projet. Nous vous dirons ce que SynRegis peut y apporter.",
      cta1b:'Prendre contact', cta2b:'Découvrir nos services',
    },
    services: {
      ol:'Nos Prestations', h1a:'Une gestion qui commence', h1b:'avant la livraison –', h1c:"et ne s'arrête jamais.",
      body:"Un syndic sérieux ne s'improvise pas au lendemain de la remise des clés. Chaque prestation SynRegis est conçue pour que le premier jour de vie de votre copropriété soit déjà maîtrisé.",
      pre_ol:'Avant la livraison', pre_h2:'Notre intervention commence avant la livraison',
      pre_sub:"Quatre étapes. Une seule promesse : le premier jour ne doit surprendre personne.",
      pre_steps:[
        {n:'1',title:"Protocole d'intégration",body:"Défini en amont avec vous, adapté à la structure de votre projet. Pas un modèle standard. Une prise en charge sur mesure."},
        {n:'2',title:'Prise en main documentaire',body:"Règlement, carnet d'entretien, contrats prestataires, plans – tout est collecté, organisé et opérationnel avant la remise des clés."},
        {n:'3',title:'Cadence de communication',body:"Établie dès le départ pour résidents, conseil syndical et professionnel de l'immobilier. Aucune zone grise."},
        {n:'4',title:'Première AG préparée',body:"Convocation conforme, ordre du jour structuré, résidents informés. La première AG donne le ton pour les années suivantes."},
      ],
      scope_ol:"Périmètre d'action", scope_h2:'Le cadre de vie que nous gérons',
      scope_items:[
        {title:'Gestion courante rigoureuse',body:'Suivi quotidien des parties communes, des équipements et des prestataires. Rien ne se règle "quand on y pense" – tout est suivi.',img:'Images/img_45.jpg'},
        {title:'Reporting financier clair et régulier',body:"Comptes transparents, appels de charges justifiés, budgets prévisionnels maîtrisés. Chaque charge doit pouvoir être expliquée à n'importe quel résident, à n'importe quel moment.",img:'Images/img_46.jpg'},
        {title:'AG préparées, tenues, actées',body:"Convocations conformes, ordre du jour structuré, procès-verbaux bilingues. Une AG bien tenue est une AG dont on n'entend plus parler.",img:'Images/img_47.jpg'},
        {title:'Communication structurée et traçable',body:"Chaque interaction entre syndic, résidents et conseil syndical est documentée. En cas de litige, vous avez un dossier. Pas des souvenirs.",img:'Images/img_48.jpg'},
      ],
      mira_ol:'Transparence & conformité', mira_kicker:'La preuve avant la parole.', mira_h2:"Une gestion qui ne repose jamais sur la confiance aveugle.",
      mira_body:"Chez SynRegis, chaque opération – comptable, juridique, technique – est documentée, horodatée et conservée. Rien ne dépend de la mémoire de quelqu'un. Les comptes sont tenus selon les règles, chaque charge est justifiable ligne par ligne, et chaque décision d'assemblée est actée dans les formes. La transparence n'est pas un geste de bonne volonté : elle est intégrée à notre façon de travailler, du premier jour à la fin du mandat.",
      mira_quote:"Une transparence vérifiable n'est plus un argument : c'est la base.",
      mira_apps:[
        {tag:'Conformité',label:'Une gestion dans les règles',desc:"Comptabilité tenue selon le cadre légal mauricien, appels de charges justifiés, assemblées convoquées et actées dans les formes. Chaque obligation respectée – et documentée."},
        {tag:'Traçabilité',label:'Tout laisse une trace',desc:"Chaque échange entre le syndic, les résidents et le conseil syndical est consigné. En cas de question ou de litige, il existe un dossier – pas des souvenirs."},
        {tag:'Accès',label:'Vos comptes, à tout moment',desc:"Comptes, documents, convocations, procès-verbaux, suivi des interventions : chaque copropriétaire accède à l'information qui le concerne, en temps réel, sans avoir à la demander."},
      ],
      cta_quote:"Chaque mandat est structuré après analyse de votre projet.", cta:'Prendre contact',
    },
    about: {
      ol:'À propos', h1a:"SynRegis n'est pas né d'une idée.", h1b:"Il est né d'une", h1em:"expérience terrain.",
      body:"SynRegis est un cabinet de gestion de copropriété premium, dédié au résidentiel haut de gamme à l'île Maurice. Une pratique rigoureuse, construite sur le terrain et fondée sur la maîtrise du droit.",
      stat1:'lots gérés en direct', stat2:'des biens · Univ. de Londres', stat2_val:'Droit',
      found_ol:'Qui sommes-nous',
      found_quote:"\"Ce n'est pas un avantage théorique. C'est ce qui évite les procès, les blocages d'AG et les conflits – avant qu'ils n'arrivent.\"",
      found_h2:"SynRegis a été fondé avec une conviction : le marché mauricien de la copropriété méritait mieux que ce qui existait.",
      found_p1:"Notre cabinet est spécialisé en droit des biens – VEFA, règlements de copropriété, servitudes.",
      found_p1em:"Chaque décision de gestion s'appuie sur un cadre légal solide, appliqué avec rigueur à chaque mandat.",
      gen_ol:'Genèse', gen_h2:'Pourquoi SynRegis existe.',
      gen_p1:"Le marché mauricien de la copropriété souffre d'un paradoxe : les syndics sont nombreux, mais rares sont ceux qui maîtrisent réellement le cadre légal dans lequel ils opèrent.",
      gen_problems:['Mandats mal rédigés','AG tenues sans conformité','Règlements inapplicables','Charges contestées'],
      gen_p2:"C'est ce constat qui est à l'origine de SynRegis. Pas une opportunité de marché abstraite – une lacune concrète, observée sur le terrain, à laquelle il manquait une réponse sérieuse.",
      diff_ol:'Ce qui nous distingue', diff_h2:'Ce qui nous distingue.',
      diff_items:[
        {title:'Une intervention en amont, pas en rattrapage.',body:"La plupart des syndics découvrent un immeuble le jour de la remise des clés. Nous intervenons avant – avec un protocole défini en amont.",img:'Images/img_51.jpg'},
        {title:'Une pratique construite ici.',body:"Nous connaissons le tissu local, les acteurs du marché, les particularités du cadre réglementaire mauricien. Construite sur ce marché, pour ce marché.",img:'Images/28072026.jpg',pos:'center top'},
        {title:"Une présence continue, pas ponctuelle.",body:"La gestion d'une copropriété n'est pas un rendez-vous annuel. C'est un suivi quotidien, une présence constante et une communication proactive.",img:'Images/img_53.jpg'},
      ],
      cta_h2:"Vous avez des questions sur notre approche ?",
      cta_body:"Nous sommes disponibles pour un échange – pour vous présenter SynRegis et comprendre votre projet.",
      cta1:'Prendre contact', cta2:'Voir nos services',
    },
    contact: {
      ol:'Contact', h1a:'Parlons de votre', h1b:'projet.',
      body:"Que vous soyez en phase de commercialisation, en cours de livraison ou déjà en gestion – il y a une porte d'entrée pour vous.",
      lbl_email:'Email', lbl_tel:'Téléphone', lbl_site:'Site', lbl_loc:'Localisation',
      how_ol:'Comment commencer', how_h2:"Trois portes d'entrée",
      doors:[
        {num:'I',title:'Prendre rendez-vous',body:"Un échange direct pour comprendre votre projet et vous proposer le niveau d'accompagnement adapté.",cta:'Réserver un créneau',img:'Images/img_54.jpg'},
        {num:'II',title:"Audit de votre copropriété",body:"Vous avez déjà un immeuble en gestion et vous n'êtes pas satisfait. SynRegis réalise un diagnostic gratuit – gestion, finances, documentation, relation résidents.",cta:'Demander un audit gratuit',img:'Images/img_55.jpg'},
        {num:'III',title:'Partenariat',body:"Vous êtes en phase de commercialisation et vous voulez sécuriser l'après-livraison. Intégrez SynRegis dès maintenant pour rassurer vos acquéreurs.",cta:"Discuter d'un partenariat",img:'Images/img_56.jpg'},
      ],
      form_h2:'Écrivez-nous directement', form_body:'Nous répondons dans les 24 heures ouvrables.',
      form_quote:'Un syndic se juge sur la durée. Commençons par une conversation.',
      footer_tag:'Cabinet de gestion de copropriété premium',
      footer_quote:'Un syndic se juge sur la durée. Commençons par une conversation.',
    },
    faq: {
      ol:'Questions fréquentes', h1a:'Tout ce que', h1b:'vous voulez savoir', h1em:'sur le métier de syndic.',
      body:"Choisir, changer, comprendre. Dix questions concrètes auxquelles SynRegis répond – pour les promoteurs, pour les acquéreurs, et pour tous ceux qui prennent leur copropriété au sérieux.",
      intro_ol:'Pour bien commencer', intro_h2:"Le métier de syndic, sans le jargon.",
      intro_body:"Le marché mauricien de la copropriété évolue vite. Voici les réponses aux questions les plus posées par les promoteurs immobiliers, les conseils syndicaux et les copropriétaires – sur le cadre légal, le choix d'un cabinet, le déroulement d'une AG, la VEFA, les charges et la plateforme digitale.",
      items:[
        {
          q:"Qu'est-ce qu'un syndic de copropriété à l'Île Maurice ?",
          a:"Un syndic de copropriété est un cabinet professionnel mandaté pour gérer les parties communes d'un immeuble : comptabilité, appels de charges, entretien, contrats prestataires, assemblées générales et application du règlement de copropriété. À l'Île Maurice, le cadre légal repose sur la loi sur la copropriété et le règlement propre à chaque résidence. Le syndic est l'interlocuteur quotidien des résidents et le garant de la pérennité financière et opérationnelle de l'immeuble."
        },
        {
          q:"Pourquoi le droit de la copropriété est-il central dans le métier de syndic ?",
          a:"La majorité des conflits en copropriété – charges contestées, AG annulées, mandats invalides, règlements inapplicables – proviennent d'un défaut de maîtrise juridique en amont. Un syndic formé en droit des biens (VEFA, règlements, servitudes) évite ces litiges en structurant correctement chaque décision dès le premier jour. SynRegis a été fondé par un diplômé en droit de l'Université de Londres spécialisé en droit des biens, précisément pour répondre à ce manque sur le marché mauricien."
        },
        {
          q:"Qu'est-ce qu'une VEFA et qu'implique-t-elle pour le syndic ?",
          a:"VEFA signifie « Vente en l'État Futur d'Achèvement » : l'acquéreur achète un bien avant ou pendant sa construction, payé par appels de fonds. Pour le syndic, la VEFA implique d'intervenir bien avant la remise des clés – protocole d'intégration, prise en main documentaire, structuration du règlement, première AG préparée – afin que le premier jour de vie de la copropriété soit déjà maîtrisé. SynRegis intègre systématiquement ces étapes amont dans ses mandats avec les promoteurs immobiliers à Maurice."
        },
        {
          q:"Pourquoi le syndic doit-il intervenir avant la livraison de l'immeuble ?",
          a:"Parce que les décisions structurantes – règlement, contrats prestataires, carnet d'entretien, première AG, modalités de communication – se prennent en amont, pas après la remise des clés. Un syndic qui découvre l'immeuble le jour de la livraison fait du rattrapage permanent, et chaque erreur initiale se paie sur la durée du mandat. À l'Île Maurice, où le marché est étroit et la réputation se propage vite, cette intervention pré-livraison distingue un cabinet sérieux. SynRegis travaille systématiquement en pré-livraison aux côtés des promoteurs immobiliers."
        },
        {
          q:"Comment choisir un syndic de copropriété à l'Île Maurice ?",
          a:"Trois critères dominent : (1) la formation juridique du cabinet – un syndic sans maîtrise du droit de la copropriété crée plus de problèmes qu'il n'en résout ; (2) l'expérience opérationnelle directe sur le marché mauricien – pas une expertise importée ; (3) la transparence de la gestion – un accès direct et en temps réel, pour chaque résident, à ses charges, documents et interventions. SynRegis remplit ces trois critères : fondé par un juriste de l'Université de Londres, 100+ lots gérés en direct avant la création du cabinet, et une gestion où chaque opération est documentée, horodatée et accessible."
        },
        {
          q:"Comment changer de syndic à l'Île Maurice ?",
          a:"Le changement de syndic se décide en assemblée générale à la majorité requise par le règlement de copropriété. Le syndic sortant doit transmettre l'ensemble de la documentation (comptes, contrats, archives) au syndic entrant. La transition est l'occasion d'auditer la gestion : SynRegis propose un audit gratuit (gestion, finances, documentation, relation résidents) aux copropriétés mauriciennes qui s'interrogent sur leur gestion actuelle."
        },
        {
          q:"Comment se déroule une assemblée générale de copropriété à Maurice ?",
          a:"L'AG annuelle réunit les copropriétaires pour voter le budget, approuver les comptes, décider des travaux et élire le conseil syndical. Une AG conforme implique : convocation respectant le délai légal et le formalisme, ordre du jour structuré, procès-verbal bilingue (FR/EN) signé séance tenante, archivage immédiat. Une AG mal préparée est l'une des principales sources de litige en copropriété – d'où l'importance d'un syndic rigoureux sur le formalisme. SynRegis prépare, tient et acte chaque AG selon ce protocole."
        },
        {
          q:"Comment sont calculées les charges de copropriété ?",
          a:"Les charges sont réparties entre copropriétaires selon les tantièmes définis dans le règlement de copropriété – généralement la quote-part de chaque lot dans les parties communes. Elles couvrent l'entretien des espaces partagés, les contrats prestataires (sécurité, jardins, ascenseurs, piscine), les assurances, les fonds de réserve et les honoraires du syndic. Chaque charge doit pouvoir être expliquée à n'importe quel résident, à n'importe quel moment – c'est la base d'un reporting transparent."
        },
        {
          q:"Comment SynRegis garantit-il la transparence et la conformité de sa gestion ?",
          a:"Chaque opération comptable, juridique et technique est documentée, horodatée et conservée. Les comptes sont tenus selon le cadre légal mauricien, chaque charge est justifiable ligne par ligne, et chaque décision d'assemblée est actée dans les formes. Les copropriétaires accèdent en temps réel aux comptes, documents, convocations et procès-verbaux qui les concernent. La transparence est intégrée à la gestion, pas ajoutée après coup – et chaque échange entre syndic, résidents et conseil syndical est consigné, de sorte qu'en cas de question ou de litige il existe un dossier, pas des souvenirs."
        },
        {
          q:"Qu'est-ce qui distingue un syndic haut de gamme ?",
          a:"Quatre marqueurs : (1) intervention en amont de la livraison, pas en rattrapage ; (2) maîtrise juridique avérée (pas seulement administrative) ; (3) reporting financier dont chaque ligne est explicable à tout moment ; (4) plateforme digitale où le résident accède directement à sa copropriété. SynRegis a été fondé exclusivement pour le segment résidentiel haut de gamme à l'Île Maurice et structure chaque mandat sur ces quatre piliers."
        }
      ],
      cta_h2:"Votre projet mérite une gestion qui préserve ce qu'il vaut.",
      cta_body:"Parlez-nous de votre projet. Nous vous dirons ce que SynRegis peut y apporter.",
      cta1:'Prendre contact',
    },
  },
  en: {
    nav: { home:'Home', services:'Services', about:'About', contact:'Contact', faq:'FAQ', cta:'Get in touch' },
    home: {
      h1a:'Your reputation',
      h1b:"doesn't stop at", h1em:'handover.',
      body:"In a market as small as Mauritius, a poorly managed building speaks for you – long after handover. SynRegis is the property management firm that protects what you have spent years building.",
      cta1:'Get in touch', cta2:'Discover our approach',
      badge:'Premium property management firm',
      risk_ol:'The invisible risk', risk_h2:'The residual risk no one names',
      risk_q:"The sale is signed. Handover is done. You move on to the next project. But for the buyer, the experience continues.",
      risk_p1:"And on an island of 1.3 million people, that experience – good or bad – gets talked about.",
      risk_p2a:"The buyer doesn't distinguish between the project and its management.",
      risk_p2b:"To them, it's the same experience. And that experience carries your name.",
      risk_stat:"inhabitants in Mauritius.", risk_stat2:"In a market where your next buyers know your previous ones, it's a risk few professionals truly account for.",
      mkt_ol:'Market context', mkt_h2:"SynRegis was born from a simple observation.",
      mkt_p1:"In Mauritius, there is no shortage of property managers. What is lacking is the legal expertise to practise this profession properly.",
      mkt_p2:"A property manager poorly trained in property law doesn't see problems coming – ",
      mkt_p2b:"they create them.", mkt_p2c:" A poorly drafted regulation, a poorly structured mandate, a general meeting decision taken without legal basis: these are disputes in the making.",
      mkt_p3:"SynRegis was founded to address this gap – with rigorous practice, legal expertise and a level of service that any demanding real estate professional has a right to expect.",
      prom_ol:'Our promise', prom_h2:'A firm that stakes its reputation on yours',
      prom_body:"SynRegis intervenes before handover, structures the living environment from day one, and manages with the level of excellence your buyers expect.",
      prom_pillars:[
        {num:'01',title:'Rigorous day-to-day management',body:'Daily monitoring of common areas, equipment and service providers. Nothing is dealt with "when we get round to it".',img:'Images/img_38.jpg'},
        {num:'02',title:'Transparent financial reporting',body:'Readable accounts, justified charges, controlled budgets. Every expense explained.',img:'Images/img_39.jpg'},
        {num:'03',title:'AGMs prepared and held',body:"Compliant notices, bilingual minutes, structured agenda. A well-run AGM goes unnoticed.",img:'Images/img_40.jpg'},
        {num:'04',title:'Traceable communication',body:'Every interaction documented. In case of dispute, you have a file – not memories.',img:'Images/img_41.jpg'},
      ],
      prom_more:'See our full service offering',
      cred:[
        {val:'100+',label:'units directly managed by the founder'},
        {val:'Law',label:'Law degree · specialisation: property law, co-ownership & off-plan'},
        {val:'1',label:'single market · a single expertise · built here'},
        {val:'High-end',label:'Exclusively high-end residential'},
      ],
      mem_ol:"The residents' memory",
      mem_h2:'A satisfied buyer is your best reference for your next project.',
      mem_p1:"An unsatisfied buyer also talks. And in a small market, their voice carries far.",
      mem_p2a:"SynRegis manages the property you deliver ", mem_p2b:"as if your next sale depended on it.", mem_p2c:" Because it does.",
      cta_h2:"Your project deserves management that preserves its value.",
      cta_body:"Tell us about your project. We'll tell you what SynRegis can bring to it.",
      cta1b:'Get in touch', cta2b:'Discover our services',
    },
    services: {
      ol:'Our Services', h1a:'Management that begins', h1b:'before handover –', h1c:"and never stops.",
      body:"A serious property manager doesn't improvise the day after handover. Every SynRegis service is designed so that the first day of your property's life is already under control.",
      pre_ol:'Before handover', pre_h2:'Our involvement begins before handover',
      pre_sub:"Four steps. One promise: the first day should surprise no one.",
      pre_steps:[
        {n:'1',title:"Integration protocol",body:"Defined upstream with you, tailored to the structure of your project. Not a standard template. A bespoke approach."},
        {n:'2',title:'Document handover',body:"Regulations, maintenance log, service contracts, plans – everything collected, organised and operational before handover."},
        {n:'3',title:'Communication cadence',body:"Established from the outset for residents, managing committee and real estate professional. No grey areas."},
        {n:'4',title:'First AGM prepared',body:"Compliant notice, structured agenda, informed residents. The first AGM sets the tone for the years ahead."},
      ],
      scope_ol:"Scope of action", scope_h2:'The living environment we manage',
      scope_items:[
        {title:'Rigorous day-to-day management',body:'Daily monitoring of common areas, equipment and service providers. Nothing is dealt with "when we get round to it" – everything is tracked.',img:'Images/img_45.jpg'},
        {title:'Clear and regular financial reporting',body:"Transparent accounts, justified service charges, controlled budgets. Every charge must be explainable to any resident, at any time.",img:'Images/img_46.jpg'},
        {title:'AGMs prepared, held, recorded',body:"Compliant notices, structured agenda, bilingual minutes. A well-run AGM is one that is never mentioned again.",img:'Images/img_47.jpg'},
        {title:'Structured and traceable communication',body:"Every interaction between manager, residents and managing committee is documented. In case of dispute, you have a file. Not memories.",img:'Images/img_48.jpg'},
      ],
      mira_ol:'Transparency & compliance', mira_kicker:'Proof before promises.', mira_h2:"Management that never asks for blind trust.",
      mira_body:"At SynRegis, every operation – financial, legal, technical – is documented, time-stamped and kept. Nothing rests on someone's memory. Accounts are kept by the book, every charge is justifiable line by line, and every general-meeting decision is recorded properly. Transparency isn't a gesture of goodwill – it's built into the way we work, from day one to the end of the mandate.",
      mira_quote:"Verifiable transparency is no longer an argument. It is the baseline.",
      mira_apps:[
        {tag:'Compliance',label:'Management by the book',desc:"Accounting kept to the Mauritian legal framework, justified service charges, meetings convened and minuted properly. Every obligation met – and documented."},
        {tag:'Traceability',label:'Everything leaves a trace',desc:"Every interaction between the manager, residents and the managing committee is logged. If a question or dispute arises, there's a file – not memories."},
        {tag:'Access',label:'Your accounts, anytime',desc:"Accounts, documents, notices, minutes, maintenance updates: every owner reaches the information that concerns them, in real time, without having to ask."},
      ],
      cta_quote:"Each mandate is structured after analysis of your project.", cta:'Get in touch',
    },
    about: {
      ol:'About', h1a:"SynRegis was not born from an idea.", h1b:"It was born from", h1em:"field experience.",
      body:"SynRegis is a premium property management firm, dedicated to high-end residential in Mauritius. A rigorous practice, built in the field and grounded in legal expertise.",
      stat1:'units directly managed', stat2:'property law · Univ. of London', stat2_val:'Law',
      found_ol:'Who we are',
      found_quote:"\"This is not a theoretical advantage. It is what prevents litigation, AGM deadlocks and conflicts – before they arise.\"",
      found_h2:"SynRegis was founded with a conviction: the Mauritian property management market deserved better than what existed.",
      found_p1:"Our firm specialises in property law – off-plan contracts, co-ownership regulations, easements.",
      found_p1em:"Every management decision is grounded in a solid legal framework, applied rigorously to each mandate.",
      gen_ol:'Origins', gen_h2:'Why SynRegis exists.',
      gen_p1:"The Mauritian property management market suffers from a paradox: managers are plentiful, but few truly master the legal framework in which they operate.",
      gen_problems:['Poorly drafted mandates','Non-compliant AGMs held','Unenforceable regulations','Disputed service charges'],
      gen_p2:"It is this observation that is at the origin of SynRegis. Not an abstract market opportunity – a concrete gap, observed in the field, that lacked a serious response.",
      diff_ol:'What sets us apart', diff_h2:'What sets us apart.',
      diff_items:[
        {title:'Involvement upstream, not catching up.',body:"Most property managers discover a building on the day of handover. We get involved before – with a protocol defined upstream.",img:'Images/img_51.jpg'},
        {title:'A practice built here.',body:"We know the local fabric, the market players, the specificities of the Mauritian regulatory framework. Built on this market, for this market.",img:'Images/28072026.jpg',pos:'center top'},
        {title:"A continuous presence, not occasional.",body:"Managing a property is not an annual appointment. It is daily monitoring, a constant presence and proactive communication.",img:'Images/img_53.jpg'},
      ],
      cta_h2:"Do you have questions about our approach?",
      cta_body:"We are available for a conversation – to introduce SynRegis and understand your project.",
      cta1:'Get in touch', cta2:'See our services',
    },
    contact: {
      ol:'Contact', h1a:"Let's talk about your", h1b:'project.',
      body:"Whether you are in the marketing phase, in the process of handover or already in management – there is an entry point for you.",
      lbl_email:'Email', lbl_tel:'Phone', lbl_site:'Website', lbl_loc:'Location',
      how_ol:'How to get started', how_h2:"Three entry points",
      doors:[
        {num:'I',title:'Book a meeting',body:"A direct conversation to understand your project and propose the right level of support.",cta:'Book a slot',img:'Images/img_54.jpg'},
        {num:'II',title:"Property audit",body:"You already have a building under management and are not satisfied. SynRegis carries out a free diagnostic – management, finances, documentation, resident relations.",cta:'Request a free audit',img:'Images/img_55.jpg'},
        {num:'III',title:'Partnership',body:"You are in the marketing phase and want to secure post-handover management. Integrate SynRegis now to reassure your buyers.",cta:"Discuss a partnership",img:'Images/img_56.jpg'},
      ],
      form_h2:'Write to us directly', form_body:'We respond within 24 business hours.',
      form_quote:'A property manager is judged over time. Let us begin with a conversation.',
      footer_tag:'Premium property management firm',
      footer_quote:'A property manager is judged over time. Let us begin with a conversation.',
    },
    faq: {
      ol:'Frequently asked questions', h1a:'Everything you', h1b:'might want to know', h1em:'about property management.',
      body:"Choosing, switching, understanding. Ten concrete questions answered by SynRegis – for developers, buyers, and anyone who takes their co-ownership seriously.",
      intro_ol:'To get started', intro_h2:"Property management in Mauritius, without the jargon.",
      intro_body:"The Mauritian co-ownership market is moving fast. Below are answers to the questions most often asked by real estate developers, managing committees and co-owners – on the legal framework, choosing a firm, how an AGM works, off-plan (VEFA), service charges and the digital platform.",
      items:[
        {
          q:"What is a property manager (syndic) in Mauritius?",
          a:"A property manager (syndic) is a professional firm mandated to manage the common areas of a building: accounting, service charges, maintenance, supplier contracts, AGMs and enforcement of the co-ownership regulations. In Mauritius the legal framework rests on the Co-Ownership Act and the regulations specific to each residence. The syndic is the residents' day-to-day point of contact and the guarantor of the financial and operational longevity of the building."
        },
        {
          q:"Why is property law central to the role of a syndic?",
          a:"The vast majority of co-ownership disputes – contested service charges, voided AGMs, invalid mandates, unenforceable regulations – stem from a lack of legal expertise upstream. A syndic trained in property law (off-plan, regulations, easements) avoids these disputes by structuring every decision correctly from day one. SynRegis was founded by a University of London law graduate specialised in property law, precisely to address this gap in the Mauritian market."
        },
        {
          q:"What is VEFA (off-plan), and what does it mean for the syndic?",
          a:"VEFA stands for the French \"Vente en l'État Futur d'Achèvement\" – a sale of property in its future state of completion. The buyer purchases a unit before or during construction, paid via staged calls. For the syndic, VEFA means getting involved well before handover – integration protocol, document handover, regulation structuring, first AGM prepared – so that day one of the co-ownership's life is already under control. SynRegis systematically integrates these upstream steps into its mandates with developers in Mauritius."
        },
        {
          q:"Why should a syndic step in before handover?",
          a:"Because the structuring decisions – regulations, supplier contracts, maintenance log, first AGM, communication cadence – are taken upstream, not after handover. A syndic who discovers the building on handover day spends the entire mandate catching up, and every initial mistake compounds over time. In Mauritius, where the market is small and reputation travels fast, pre-handover involvement is what sets a serious firm apart. SynRegis works in pre-handover alongside developers, in four defined steps."
        },
        {
          q:"How do I choose a property manager (syndic) in Mauritius?",
          a:"Three criteria dominate: (1) the firm's legal training – a syndic without mastery of property law creates more problems than it solves; (2) direct operational experience on the Mauritian market – not imported expertise; (3) transparent management – direct, real-time access for every resident to their charges, documents and maintenance updates. SynRegis meets all three: founded by a University of London lawyer, 100+ units directly managed before the firm was created, and a management approach where every operation is documented, time-stamped and accessible."
        },
        {
          q:"How do I switch property managers in Mauritius?",
          a:"Changing the syndic is decided in a general meeting at the majority required by the co-ownership regulations. The outgoing syndic must hand over the entire documentation set (accounts, contracts, archives) to the incoming syndic. A transition is the right moment to audit the management: SynRegis offers a free diagnostic (management, finances, documentation, resident relations) to Mauritian co-ownerships questioning their current management."
        },
        {
          q:"How does a co-ownership general meeting (AGM) work in Mauritius?",
          a:"The annual general meeting brings owners together to vote the budget, approve the accounts, decide on works and elect the managing committee. A compliant AGM means: notice respecting the statutory delay and formalities, a structured agenda, bilingual (FR/EN) minutes signed on the spot, immediate archiving. A poorly run AGM is one of the leading sources of co-ownership disputes – hence the importance of a syndic rigorous on formalities. SynRegis prepares, holds and records every AGM to this protocol."
        },
        {
          q:"How are co-ownership service charges calculated?",
          a:"Charges are split between co-owners according to the shares (\"tantièmes\") defined in the co-ownership regulations – typically each unit's proportional share of the common areas. They cover the maintenance of shared spaces, supplier contracts (security, gardens, lifts, pool), insurance, reserve funds and the syndic's fees. Every charge must be explainable to any resident, at any time – that is the baseline for transparent reporting."
        },
        {
          q:"How does SynRegis ensure transparent and compliant management?",
          a:"Every financial, legal and technical operation is documented, time-stamped and kept. Accounts are held to the Mauritian legal framework, every charge is justifiable line by line, and every general-meeting decision is recorded properly. Owners access in real time the accounts, documents, notices and minutes that concern them. Transparency is built into the management, not added afterwards – and every interaction between manager, residents and the managing committee is logged, so that if a question or dispute arises there is a file, not memories."
        },
        {
          q:"What sets a premium property manager apart?",
          a:"Four markers: (1) involvement upstream of handover, not catching up; (2) demonstrable legal expertise (not just administrative skill); (3) financial reporting where every line is explainable at any moment; (4) a digital platform giving the resident direct access to their building. SynRegis was founded exclusively for the high-end residential segment in Mauritius and structures every mandate around these four pillars."
        }
      ],
      cta_h2:"Your project deserves management that preserves its value.",
      cta_body:"Tell us about your project. We'll tell you what SynRegis can bring to it.",
      cta1:'Get in touch',
    },
  },
};

/* Real flag graphics (inline SVG) — emoji flags don't render on Windows. */
function FlagFR({ size = 22 }) {
  return (
    <svg width={size} height={size * 0.7} viewBox="0 0 3 2"
      style={{ borderRadius:2, display:'block', flexShrink:0,
        boxShadow:'0 0 0 1px rgba(0,0,0,0.08)' }} aria-hidden="true">
      <rect width="3" height="2" fill="#fff" />
      <rect width="1" height="2" fill="#0055A4" />
      <rect x="2" width="1" height="2" fill="#EF4135" />
    </svg>
  );
}
function FlagGB({ size = 22, uid = 'gb' }) {
  return (
    <svg width={size} height={size * 0.7} viewBox="0 0 60 30"
      style={{ borderRadius:2, display:'block', flexShrink:0, overflow:'hidden',
        boxShadow:'0 0 0 1px rgba(0,0,0,0.08)' }} aria-hidden="true">
      <clipPath id={`${uid}-d`}>
        <path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" />
      </clipPath>
      <rect width="60" height="30" fill="#012169" />
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
      <path d="M0,0 L60,30 M60,0 L0,30" clipPath={`url(#${uid}-d)`} stroke="#C8102E" strokeWidth="4" />
      <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" />
      <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
    </svg>
  );
}
function Flag({ code, uid }) {
  return code === 'fr' ? <FlagFR /> : <FlagGB uid={uid} />;
}

function LangSwitcher({ lang, setLang }) {
  const opts = { fr:{ name:'Français' }, en:{ name:'English' } };
  const order = ['fr', 'en'];
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} style={{ position:'relative', display:'inline-flex' }}>
      <button
        onClick={() => setOpen(o => !o)}
        title="Language"
        aria-label="Select language"
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          background:'none', border:'none', padding:'4px 6px',
          cursor:'pointer', display:'inline-flex', alignItems:'center', gap:5,
          lineHeight:1, opacity: open ? 1 : 0.7, transition:'opacity 0.2s',
          userSelect:'none',
        }}
        onMouseEnter={e => e.currentTarget.style.opacity='1'}
        onMouseLeave={e => e.currentTarget.style.opacity = open ? '1' : '0.7'}
      >
        <Flag code={lang} uid="lang-btn" />
        <span style={{
          fontSize:13, lineHeight:1, color:'#d5ba81', transition:'transform 0.2s',
          transform: open ? 'rotate(180deg)' : 'none',
        }}>▾</span>
      </button>
      {open && (
        <ul
          role="listbox"
          style={{
            position:'absolute', top:'calc(100% + 8px)', right:0, margin:0, padding:'4px',
            listStyle:'none', background:'#FAF7F0',
            border:'1px solid rgba(28,26,21,0.12)', borderRadius:2,
            boxShadow:'0 8px 24px rgba(0,0,0,0.12)', zIndex:1000,
          }}
        >
          {order.map(code => {
            const o = opts[code];
            const active = code === lang;
            return (
              <li
                key={code}
                role="option"
                aria-selected={active}
                onClick={() => { setLang(code); setOpen(false); }}
                title={o.name}
                aria-label={o.name}
                style={{
                  display:'flex', alignItems:'center', justifyContent:'center', padding:'8px 12px',
                  cursor:'pointer',
                  background: active ? 'rgba(213,186,129,0.18)' : 'transparent',
                  transition:'background 0.15s', borderRadius:2,
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(213,186,129,0.28)'}
                onMouseLeave={e => e.currentTarget.style.background = active ? 'rgba(213,186,129,0.18)' : 'transparent'}
              >
                <Flag code={code} uid={`lang-opt-${code}`} />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* ─── IMAGE PLACEHOLDER ─── */
function ImgPh({ w, h, label, dark, style }) {
  return (
    <div className={dark ? 'img-ph-dark' : 'img-ph'} style={{ width: w, height: h, ...style }}>
      <svg className="img-ph-icon" width="28" height="28" viewBox="0 0 28 28" fill="none">
        <rect x="2" y="6" width="24" height="18" rx="1" stroke={dark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.3)'} strokeWidth="1.2" />
        <circle cx="9" cy="12" r="2" stroke={dark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.3)'} strokeWidth="1.2" />
        <path d="M2 20 L8 14 L13 19 L18 15 L26 22" stroke={dark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.3)'} strokeWidth="1.2" fill="none" />
      </svg>
      <div className={dark ? 'img-ph-label-light' : 'img-ph-label'}>{label}</div>
    </div>);

}

/* ─── LOGO SVG (inline gold keys) ─── */
function LogoMark({ size = 40, color = '#C4A052' }) {
  return (
    <svg width={size} height={size * 0.85} viewBox="0 0 120 102" fill="none">
      {/* Simplified crossed keys */}
      <g stroke={color} strokeWidth="3" fill="none">
        {/* Key 1 */}
        <path d="M25 20 L75 70" strokeWidth="4" />
        <rect x="18" y="10" width="16" height="16" rx="8" stroke={color} strokeWidth="3.5" />
        <rect x="12" y="6" width="28" height="28" rx="10" stroke={color} strokeWidth="2.5" />
        <path d="M66 66 L70 70 L66 74 L70 78" strokeWidth="3" />
        <path d="M72 66 L76 70 L72 74" strokeWidth="3" />
        {/* Key 2 */}
        <path d="M95 20 L45 70" strokeWidth="4" />
        <rect x="86" y="10" width="16" height="16" rx="8" stroke={color} strokeWidth="3.5" />
        <rect x="80" y="6" width="28" height="28" rx="10" stroke={color} strokeWidth="2.5" />
        <path d="M54 66 L50 70 L54 74 L50 78" strokeWidth="3" />
        <path d="M48 66 L44 70 L48 74" strokeWidth="3" />
        {/* Center keyhole */}
        <circle cx="60" cy="52" r="3" fill={color} strokeWidth="0" />
      </g>
      {/* Filigree top decorative */}
      <path d="M35 12 Q60 2 85 12" stroke={color} strokeWidth="1.2" fill="none" opacity="0.5" />
    </svg>);

}

/* ─── NAV ─── */
function Nav({ page, setPage, lang, setLang }) {
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = React.useState(false);

  const t = T[lang];
  const links = [
    { id: 'home', label: t.nav.home },
    { id: 'services', label: t.nav.services },
    { id: 'about', label: t.nav.about },
    { id: 'contact', label: t.nav.contact },
    { id: 'faq', label: t.nav.faq }
  ];

  const navigate = (id) => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    setPage(id);
    setMenuOpen(false);
  };
  // Real <a href> so crawlers/link-graph tools see an actual internal link,
  // while JS-enabled visitors still get the instant SPA transition via navigate().
  const hrefFor = (id) => (id === 'home' ? '/' : `/${id}`);
  const go = (id) => (e) => { e.preventDefault(); navigate(id); };
  const navLogoRef = React.useRef(null);

  // Transparent + white logo while floating over a page's own full-bleed photo
  // hero (home, services, about); solid cream + navy logo once scrolled past it,
  // or on any other page. Threshold is the hero section's own measured height
  // (100vh on all three) minus the nav's height, rather than a fixed vh guess,
  // so it lands right as the hero's bottom edge reaches the nav.
  const immersivePages = page === 'home' || page === 'services' || page === 'about';
  const [scrolled, setScrolled] = React.useState(!immersivePages);
  React.useEffect(() => {
    if (!immersivePages) { setScrolled(true); return; }
    const navHeight = isMobile ? 70 : 120;
    const heroEl = document.querySelector('.page > section:first-of-type');
    const heroHeight = heroEl ? heroEl.getBoundingClientRect().height : window.innerHeight;
    const onScroll = () => setScrolled(window.scrollY > heroHeight - navHeight);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [page, isMobile]);
  const transparent = immersivePages && !scrolled;

  return (
    // Transparent over the home hero photo (white logo/links), solid cream banner
    // once scrolled past it or on any other page (navy logo/links) — fixed at the
    // top of every page. The intro lands the logo right here.
    <nav style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: isMobile ? '0 20px' : '0 64px',
      height: isMobile ? '70px' : '120px',
      background: transparent ? 'transparent' : 'rgba(250, 248, 244, 0.98)',
      backdropFilter: transparent ? 'none' : 'blur(12px)',
      WebkitBackdropFilter: transparent ? 'none' : 'blur(12px)',
      borderBottom: transparent ? '1px solid transparent' : '1px solid rgba(196,160,82,0.25)',
      boxShadow: transparent ? 'none' : '0 1px 24px rgba(0,0,0,0.06)',
      transition: 'background 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease'
    }}>

      {/* Logo – data-nav-logo must stay the plain crest+wordmark mark (matches the intro's
          traced/filled mark 1:1) so the intro's fly-to-navbar handoff lands at the exact
          scale with no pop. The tagline below it is the "Gestion de copropriétés" artwork
          traced out of the brand's official full-colour vector lockup (paths isolated from
          design-docs/Internal/Logos/Vector/SynRegis - Full Colour.svg), sized and gapped to
          the exact proportions of that source file — not a re-typeset approximation, and not
          translated (the brand mark stays French regardless of site language). It rolls in
          just after the intro hands off, so it never affects the intro's landing measurement.
          White variant swaps in while `transparent` (floating over the home hero) — same
          asset pair the intro's own white-unroll hand-off uses, so they match at the dock. */}
      <a ref={navLogoRef} href="/" onClick={go('home')} style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: isMobile ? 6 : 8, textDecoration: 'none', color: 'inherit' }}>
        <img data-nav-logo src={transparent ? 'Images/synregis_mark_white.svg' : 'Images/synregis_mark.png'} alt="SynRegis" style={{ height: isMobile ? 54 : 78, objectFit: 'contain', objectPosition: 'left' }} />
        <div data-nav-tagline className="nav-tagline-mask">
          <img className="nav-tagline-reveal" src={transparent ? 'Images/synregis_tagline_white.svg' : 'Images/synregis_tagline.svg'} alt="Gestion de copropriétés" style={{ height: isMobile ? 5 : 8, display: 'block' }} />
        </div>
      </a>

      {/* Desktop links — links + CTA grouped, with the lang switcher pushed to the far right corner */}
      {!isMobile && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 28 }}>
          <div style={{ display: 'flex', gap: 40, alignItems: 'center' }}>
            {links.map((l) => (
              <a key={l.id} href={hrefFor(l.id)} className={`nav-link ${transparent ? 'nav-link-light' : ''} ${page === l.id ? 'active' : ''}`} onClick={go(l.id)} style={{ textDecoration: 'none' }}>
                {l.label}
              </a>
            ))}
            <a href={hrefFor('contact')} className={`nav-cta ${transparent ? 'nav-cta-light' : ''}`} onClick={go('contact')} style={{ textDecoration: 'none', display: 'inline-block' }}>{t.nav.cta}</a>
          </div>
          <LangSwitcher lang={lang} setLang={setLang} />
        </div>
      )}

      {/* Hamburger button — mobile only */}
      {isMobile && (
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8, display: 'flex', flexDirection: 'column', gap: 5 }}
          aria-label="Menu"
        >
          <span style={{ display: 'block', width: 24, height: 2, background: transparent ? '#fff' : '#14120e', transition: 'transform 0.2s', transform: menuOpen ? 'rotate(45deg) translate(5px, 5px)' : 'none' }} />
          <span style={{ display: 'block', width: 24, height: 2, background: transparent ? '#fff' : '#14120e', transition: 'opacity 0.2s', opacity: menuOpen ? 0 : 1 }} />
          <span style={{ display: 'block', width: 24, height: 2, background: transparent ? '#fff' : '#14120e', transition: 'transform 0.2s', transform: menuOpen ? 'rotate(-45deg) translate(5px, -5px)' : 'none' }} />
        </button>
      )}

      {/* Mobile dropdown menu */}
      {isMobile && menuOpen && (
        <div style={{
          position: 'fixed', top: 70, left: 0, right: 0,
          background: 'rgba(250, 248, 244, 0.99)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(196,160,82,0.25)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          zIndex: 99,
          display: 'flex', flexDirection: 'column',
          padding: '8px 20px 24px'
        }}>
          {links.map((l) => (
            <a
              key={l.id}
              href={hrefFor(l.id)}
              onClick={go(l.id)}
              style={{
                background: 'none', border: 'none',
                borderBottom: '1px solid rgba(196,160,82,0.15)',
                padding: '16px 0', textAlign: 'left', cursor: 'pointer',
                fontSize: 13, letterSpacing: '0.12em', textTransform: 'uppercase',
                color: page === l.id ? '#14120e' : 'rgba(20,18,14,0.55)',
                fontFamily: "'DM Sans', sans-serif", fontWeight: 400,
                textDecoration: 'none', display: 'block'
              }}
            >
              {l.label}
            </a>
          ))}
          <a
            href={hrefFor('contact')}
            className="nav-cta"
            onClick={go('contact')}
            style={{ padding: '13px 20px', marginTop: 16, alignSelf: 'stretch', textDecoration: 'none', textAlign: 'center' }}
          >
            {t.nav.cta}
          </a>
          <div style={{ marginTop: 16, alignSelf: 'flex-start' }}>
            <LangSwitcher lang={lang} setLang={setLang} />
          </div>
        </div>
      )}
    </nav>
  );
}

/* ══════════════════════════════
   PAGE HOME
══════════════════════════════ */
// True on a phone/small screen held upright: the home hero then plays the portrait video.
const usePortraitPhone = () => {
  const q = '(max-width: 768px) and (orientation: portrait)';
  const [on, setOn] = React.useState(() => window.matchMedia(q).matches);
  React.useEffect(() => {
    const m = window.matchMedia(q);
    const h = () => setOn(m.matches);
    h();
    m.addEventListener ? m.addEventListener('change', h) : m.addListener(h);
    window.addEventListener('resize', h);
    window.addEventListener('orientationchange', h);
    return () => {
      m.removeEventListener ? m.removeEventListener('change', h) : m.removeListener(h);
      window.removeEventListener('resize', h);
      window.removeEventListener('orientationchange', h);
    };
  }, []);
  return on;
};

function PageHome({ setPage, lang }) {
  const t = T[lang];
  const isMobile = useIsMobile();
  const portraitPhone = usePortraitPhone();
  const pageRef = React.useRef(null);
  useScrollReveal(pageRef);
  const heroImgRef = React.useRef(null);
  useImageReveal(heroImgRef, { y: 0 });
  return (
    <div ref={pageRef} className="page">
      {/* HERO */}
      <section style={{ minHeight: '100svh', background: '#1A1510', position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'flex-end', paddingTop: isMobile?70:120 }}>
        {/* Full-bleed background video */}
        <div ref={heroImgRef} style={{ position: 'absolute', inset: 0 }}>
          <video key={portraitPhone ? 'portrait' : 'wide'} autoPlay={!introRunning()} muted loop playsInline preload="auto" poster={portraitPhone ? 'Images/poster_home_hero_portrait.jpg' : 'Images/poster_home_hero.jpg'} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: portraitPhone ? 'center center' : (isMobile ? '60% 60%' : 'center 65%'), display: 'block' }}>
            <source src={portraitPhone ? 'Images/video_home_hero_portrait.mp4' : 'Images/video_home_hero.mp4'} type="video/mp4" />
          </video>
          {/* Trust-forward navy scrim — darkest where the copy sits (bottom-left), lightest toward the upper right so the landscape still reads */}
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(26,21,16,0.20) 0%, rgba(26,21,16,0.30) 40%, rgba(26,21,16,0.90) 100%)' }}></div>
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(26,21,16,0.82) 0%, rgba(26,21,16,0.30) 45%, rgba(26,21,16,0) 70%)' }}></div>
        </div>

        {/* Text, sitting directly on the photo */}
        <div style={{ position: 'relative', zIndex: 1, padding: isMobile?'0 20px 56px':'0 80px 100px', maxWidth: isMobile?'100%':560, display: 'flex', flexDirection: 'column', gap: isMobile?24:28 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <span className="overline">{t.home.badge}</span>
            <span className="gold-rule-long"></span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <p className="body-text-light" style={{ margin: 0 }}>
              {t.home.body}
            </p>
            <h1 className="body-text-light" style={{ margin: 0, fontWeight: 300 }}>
              {t.home.h1a} {t.home.h1b}<em style={{ color: 'var(--gold)', fontStyle: 'italic' }}> {t.home.h1em}</em>
            </h1>
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <button className="btn-primary" onClick={() => setPage('contact')}>{t.home.cta1}</button>
            <button className="btn-ghost" onClick={() => setPage('services')}>{t.home.cta2}</button>
          </div>
        </div>

        {/* Gold corner accent, echoing the frame motif used on the site's other photo heroes */}
        <div style={{ position: 'absolute', top: 152, right: 64, width: 80, height: 80, borderTop: '2px solid var(--gold)', borderRight: '2px solid var(--gold)', opacity: 0.5, display: isMobile?'none':'block' }}></div>
      </section>

      {/* BLOC PROBLÈME */}
      <section style={{ background: 'var(--cream)', padding: isMobile?'40px 20px':'120px 80px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile?'minmax(0,1fr)':'1fr 2fr', gap: isMobile?40:80, maxWidth: 1300, margin: '0 auto', alignItems: 'start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20, position: isMobile?'static':'sticky', top: 120 }}>
            <span className="overline">{t.home.risk_ol}</span>
            <span className="gold-rule-long"></span>
            <h2 className="section-title" style={{ fontSize: isMobile?32:48 }}>{t.home.risk_h2}</h2>
            <img src="Images/img_36.jpg" alt="Parties communes" style={{ width: '100%', height: isMobile?180:260, objectFit: 'cover', marginTop: 24, display: 'block' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 36, paddingTop: 8 }}>
            <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 26, lineHeight: 1.5, color: 'var(--navy)', fontStyle: 'italic', borderLeft: '3px solid var(--gold)', paddingLeft: 28 }}>
              {t.home.risk_q}
            </p>
            <p className="body-text">
              {t.home.risk_p1}
            </p>
            <div style={{ height: 1, background: 'var(--cream-dark)' }}></div>
            <p className="body-text">
              <strong style={{ color: 'var(--navy)', fontWeight: 500 }}>{t.home.risk_p2a}</strong> {t.home.risk_p2b}
            </p>
            {/* Pull stat */}
            <div style={{ background: 'var(--navy-dark)', padding: isMobile?'24px 20px':'40px 48px', display: 'flex', gap: isMobile?16:32, alignItems: 'center', marginTop: 16, flexDirection: isMobile?'column':'row' }}>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: isMobile?48:64, fontWeight: 300, color: 'var(--gold)', lineHeight: 1 }}>1,3M</div>
              <div style={{ borderLeft: isMobile?'none':'1px solid rgba(255,255,255,0.15)', paddingLeft: isMobile?0:32 }}>
                <div style={{ fontSize: 13, color: '#fff', lineHeight: 1.5, fontFamily: 'DM Sans', fontWeight: 300 }}>
                  {t.home.risk_stat}<br />
                  <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>{t.home.risk_stat2}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BLOC CONTEXTE MARCHÉ */}
      <section style={{ padding: isMobile?'40px 20px':'120px 80px', position: 'relative', overflow: 'hidden' }} className="grain">
        {/* Full-bleed background photo — the same image used on the hero before it moved to video */}
        <div style={{ position: 'absolute', inset: 0, zIndex: -2 }}>
          <img src="Images/le-morne-mauritius-hero.jpg" alt="Résidence de standing au pied du Morne, Île Maurice – gestion de copropriété SynRegis" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: isMobile ? '60% 60%' : 'center 65%', display: 'block', filter: 'saturate(0.8) contrast(1.06) brightness(0.95)' }} />
        </div>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(100deg, rgba(26,21,16,0.90) 0%, rgba(26,21,16,0.68) 55%, rgba(26,21,16,0.40) 100%)', zIndex: -1 }}></div>
        {/* BG decorative */}
        <div style={{ position: 'absolute', right: isMobile?-200:-100, top: isMobile?-200:-100, width: isMobile?300:600, height: isMobile?300:600, borderRadius: '50%', border: '1px solid rgba(196,160,82,0.08)' }}></div>
        <div style={{ position: 'absolute', right: isMobile?-150:-60, top: isMobile?-150:-60, width: isMobile?200:400, height: isMobile?200:400, borderRadius: '50%', border: '1px solid rgba(196,160,82,0.06)' }}></div>
        <div style={{ maxWidth: 1300, margin: '0 auto', position: 'relative' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 28, maxWidth: isMobile?'100%':680 }}>
            <span className="overline">{t.home.mkt_ol}</span>
            <span className="gold-rule-long"></span>
            <h2 className="section-title-light" style={{ fontSize: isMobile?32:52 }}>{t.home.mkt_h2}</h2>
            <p className="body-text-light">
              {t.home.mkt_p1}
            </p>
            <p className="body-text-light">
              {t.home.mkt_p2}<span style={{ color: 'rgba(255,255,255,0.9)' }}>{t.home.mkt_p2b}</span>{t.home.mkt_p2c}
            </p>
            <p className="body-text-light">
              {t.home.mkt_p3}
            </p>
          </div>
        </div>
      </section>

      {/* BLOC SOLUTION — 4 PILIERS */}
      <section style={{ background: 'var(--cream)', padding: isMobile?'40px 20px':'120px 80px' }}>
        <div style={{ maxWidth: 1300, margin: '0 auto' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: isMobile?40:72, alignItems: 'center', textAlign: 'center' }}>
            <span className="overline">{t.home.prom_ol}</span>
            <span className="gold-rule" style={{ alignSelf: 'center' }}></span>
            <h2 className="section-title" style={{ fontSize: isMobile?28:52 }}>{t.home.prom_h2}</h2>
            <p className="body-text" style={{ maxWidth: 560, marginTop: 8 }}>{t.home.prom_body}</p>
          </div>
          {/* 4 pillars grid */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile?'minmax(0,1fr) minmax(0,1fr)':'repeat(4, 1fr)', gap: 2 }}>
            
{t.home.prom_pillars.map((p) =>
            <div key={p.num} style={{ background: 'rgba(255,255,255,0.72)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.5)', display: 'flex', flexDirection: 'column', transition: 'transform 0.2s' }}>
                <img src={p.img} alt={p.title} style={{ width: '100%', height: isMobile?140:200, objectFit: 'cover', display: 'block', objectPosition: 'center center' }} />
                <div style={{ padding: isMobile ? '20px 16px 28px' : '28px 28px 36px' }}>
                  <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 13, color: 'var(--gold)', letterSpacing: '0.2em', marginBottom: 12 }}>{p.num}</div>
                  <div style={{ height: 1, background: 'var(--cream-dark)', marginBottom: 16 }}></div>
                  <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 500, color: 'var(--navy-dark)', lineHeight: 1.2, marginBottom: 12 }}>{p.title}</h3>
                  <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-muted)', fontWeight: 300 }}>{p.body}</p>
                </div>
              </div>
            )}
          </div>
          <div style={{ marginTop: 48, display: 'flex', justifyContent: 'center' }}>
            <button className="btn-ghost-navy" onClick={() => setPage('services')}>{t.home.prom_more}</button>
          </div>
        </div>
      </section>

      {/* BLOC CRÉDIBILITÉ */}
      <section style={{ background: 'var(--navy-dark)', padding: isMobile?'40px 20px':'80px', borderTop: '1px solid rgba(196,160,82,0.15)', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, backdropFilter: 'blur(0px)' }}></div>
        <div style={{ maxWidth: 1300, margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile?'minmax(0,1fr) minmax(0,1fr)':'repeat(4, 1fr)', gap: 0 }}>
          {t.home.cred.map((f, i) =>
          <div key={i} style={{ padding: '40px 40px', borderLeft: (!isMobile && i > 0) ? '1px solid rgba(255,255,255,0.06)' : 'none', display: 'flex', flexDirection: 'column', gap: 10, background: i % 2 === 0 ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.03)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: isMobile?36:48, fontWeight: 300, color: 'var(--gold)', lineHeight: 1 }}>{f.val}</div>
              <div style={{ height: 1, width: 30, background: 'var(--gold)', opacity: 0.5 }}></div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.5, fontWeight: 300 }}>{f.label}</div>
            </div>
          )}
        </div>
      </section>

      {/* BLOC MÉMOIRE DES RÉSIDENTS */}
      <section style={{ background: 'var(--cream)', padding: isMobile?'40px 20px':'120px 80px' }}>
        <div style={{ maxWidth: 1300, margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile?'minmax(0,1fr)':'1fr 1fr', gap: isMobile?40:80, alignItems: 'center' }}>
          <img src="Images/img_42.jpg" alt="Résidents d'une copropriété haut de gamme à l'Île Maurice" style={{ width: '100%', height: isMobile?280:520, objectFit: 'cover', display: 'block' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
            <span className="overline">{t.home.mem_ol}</span>
            <span className="gold-rule-long"></span>
            <h2 className="section-title" style={{ fontSize: isMobile?32:48 }}>{t.home.mem_h2}</h2>
            <div style={{ height: 1, background: 'var(--cream-dark)' }}></div>
            <p className="body-text">{t.home.mem_p1}</p>
            <p className="body-text">{t.home.mem_p2a}<strong style={{ color: 'var(--navy)', fontWeight: 500 }}>{t.home.mem_p2b}</strong>{t.home.mem_p2c}</p>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section style={{ background: 'var(--navy-dark)', padding: isMobile?'40px 20px':'120px 80px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', width: isMobile?300:700, height: isMobile?300:700, borderRadius: '50%', border: '1px solid rgba(196,160,82,0.06)', pointerEvents: 'none' }}></div>
        <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', width: isMobile?200:500, height: isMobile?200:500, borderRadius: '50%', border: '1px solid rgba(196,160,82,0.08)', pointerEvents: 'none' }}></div>
        <div style={{ position: 'relative', zIndex: 1, maxWidth: 700, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 24, alignItems: 'center' }}>
          <img src="Images/img_43.png" alt="SynRegis" style={{ height: isMobile?70:130, opacity: 0.95, marginBottom: isMobile?8:16 }} />
          <h2 className="section-title-light" style={{ fontSize: isMobile?32:52 }}>{t.home.cta_h2}</h2>
          <p className="body-text-light" style={{ maxWidth: 480 }}>{t.home.cta_body}</p>
          <div style={{ display: 'flex', gap: 16, marginTop: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
            <button className="btn-primary" onClick={() => setPage('contact')}>{t.home.cta1b}</button>
            <button className="btn-ghost" onClick={() => setPage('services')}>{t.home.cta2b}</button>
          </div>
        </div>
      </section>
    </div>);

}

/* ══════════════════════════════
   PAGE SERVICES
══════════════════════════════ */
function PageServices({ setPage, lang }) {
  const t = T[lang];
  const isMobile = useIsMobile();
  const revealRef = React.useRef(null);
  useGSAPReveal(revealRef, 0.9);
  const pageRef = React.useRef(null);
  useScrollReveal(pageRef);
  const heroBgRef = React.useRef(null);
  useImageReveal(heroBgRef, { y: 0 });
  return (
    <div ref={pageRef} className="page">
      {/* HERO */}
      <section style={{ minHeight: '100svh', background: 'var(--navy-dark)', display: 'flex', alignItems: 'flex-end', paddingTop: isMobile?70:80, position: 'relative', overflow: 'hidden' }}>
        <div ref={heroBgRef} style={{ position: 'absolute', inset: 0 }}>
          <video autoPlay={!introRunning()} muted loop playsInline preload="auto" poster="Images/poster_services_hero.jpg" style={{ width: '100%', height: '100%', minHeight: isMobile?'100svh':500, objectFit: 'cover', display: 'block' }}>
            <source src="Images/video_services_hero.mp4" type="video/mp4" />
          </video>
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, rgba(13,22,39,0.92) 55%, rgba(13,22,39,0.3))' }}></div>
        </div>
        <div style={{ position: 'relative', zIndex: 1, padding: isMobile?'40px 20px':'80px 80px 100px', maxWidth: isMobile?'100%':760 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28 }}>
            <span className="overline">{t.services.ol}</span>
          </div>
          <div ref={revealRef}>
            <div className="line-mask"><h1 className="reveal-text" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: isMobile?40:68, fontWeight: 400, lineHeight: 1.1, color: '#fff', margin: 0 }}>{t.services.h1a}</h1></div>
            <div className="line-mask"><div className="reveal-text nth2" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: isMobile?40:68, fontWeight: 400, lineHeight: 1.1, color: '#fff', margin: 0 }}>{t.services.h1b}</div></div>
            <div className="line-mask" style={{ marginBottom: 28 }}><div className="reveal-text nth3" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: isMobile?40:68, fontWeight: 400, lineHeight: 1.1, color: '#fff', margin: 0 }}><em style={{ color: 'var(--gold)' }}>{t.services.h1c}</em></div></div>
          </div>
          <p className="body-text-light" style={{ maxWidth: 520 }}>{t.services.body}</p>
        </div>
      </section>

      {/* PROTOCOLE — 4 ÉTAPES */}
      <section style={{ position: 'relative', overflow: 'hidden', padding: isMobile?'40px 20px':'120px 80px' }}>
        <video autoPlay={!introRunning()} muted loop playsInline preload="auto" poster="Images/poster_avant_livraison.jpg" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', zIndex: -2 }}>
          <source src="Images/video_avant_livraison.mp4" type="video/mp4" />
        </video>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(15,12,8,0.82) 0%, rgba(15,12,8,0.72) 45%, rgba(15,12,8,0.85) 100%)', zIndex: -1 }}></div>
        <div style={{ maxWidth: 1300, margin: '0 auto', position: 'relative' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: isMobile?40:80 }}>
            <span className="overline">{t.services.pre_ol}</span>
            <span className="gold-rule-long"></span>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile?'minmax(0,1fr)':'1fr 1fr', gap: isMobile?12:16 }}>
              <h2 className="section-title-light" style={{ fontSize: isMobile?32:48 }}>{t.services.pre_h2}</h2>
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                <p style={{ fontSize: 15, lineHeight: 1.75, color: 'rgba(255,255,255,0.72)', fontWeight: 300 }}>{t.services.pre_sub}</p>
              </div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile?'minmax(0,1fr) minmax(0,1fr)':'repeat(4, 1fr)', gap: isMobile?'32px 12px':0, position: 'relative' }}>
            <div style={{ position: 'absolute', top: 28, left: '12.5%', right: '12.5%', height: 1, background: 'var(--gold)', opacity: isMobile?0:0.35, display: isMobile?'none':'block' }}></div>
            {t.services.pre_steps.map((s) =>
            <div key={s.n} style={{ padding: '0 28px 0 0', display: 'flex', flexDirection: 'column', gap: 24 }}>
                <div style={{ width: 56, height: 56, border: '1px solid var(--gold)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}>
                  <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 300, color: 'var(--gold)' }}>{s.n}</span>
                </div>
                <div style={{ height: 1, background: 'rgba(255,255,255,0.18)' }}></div>
                <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 500, color: '#fff', lineHeight: 1.2 }}>{s.title}</h3>
                <p style={{ fontSize: 13, lineHeight: 1.65, color: 'rgba(255,255,255,0.62)', fontWeight: 300 }}>{s.body}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* PÉRIMÈTRE D'ACTION */}
      <section style={{ background: 'var(--navy)', padding: isMobile?'40px 20px':'120px 80px' }}>
        <div style={{ maxWidth: 1300, margin: '0 auto' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: isMobile?40:72 }}>
            <span className="overline">{t.services.scope_ol}</span>
            <span className="gold-rule-long"></span>
            <h2 className="section-title-light" style={{ fontSize: isMobile?32:52 }}>{t.services.scope_h2}</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile?'minmax(0,1fr)':'1fr 1fr', gap: isMobile?8:2 }}>
            {t.services.scope_items.map((s, i) =>
            <div key={i} style={{ backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)', background: 'rgba(255,255,255,0.05)', borderTop: '1px solid rgba(255,255,255,0.08)', padding: 0, display: 'grid', gridTemplateColumns: isMobile?'minmax(0,1fr)':'200px 1fr', gap: isMobile?8:0 }}>
                <img src={s.img} alt={s.title} style={{ width: isMobile?'100%':200, height: isMobile?140:220, objectFit: 'cover', objectPosition: 'center center', display: 'block', flexShrink: 0 }} />
                <div style={{ padding: '32px 32px' }}>
                  <div style={{ height: 1, width: 30, background: 'var(--gold)', marginBottom: 16 }}></div>
                  <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, fontWeight: 500, color: '#fff', lineHeight: 1.2, marginBottom: 14 }}>{s.title}</h3>
                  <p style={{ fontSize: 13, lineHeight: 1.65, color: 'rgba(255,255,255,0.55)', fontWeight: 300 }}>{s.body}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* BLOC TRANSPARENCE & CONFORMITÉ */}
      <section style={{ background: 'var(--cream)', padding: isMobile?'40px 20px':'120px 80px', position: 'relative', overflow: 'hidden' }}>
        {/* Subtle gold gradient backdrop — SynRegis accent colour */}
        <div style={{ position: 'absolute', top: -200, right: -200, width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(196,160,82,0.10) 0%, rgba(196,160,82,0) 70%)', pointerEvents: 'none' }}></div>
        <div style={{ maxWidth: 1300, margin: '0 auto', position: 'relative' }}>
          {/* Header row */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile?'minmax(0,1fr)':'1fr 1fr', gap: isMobile?40:80, alignItems: 'start', marginBottom: isMobile?40:72 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              <span className="overline" style={{ color: 'var(--gold)' }}>{t.services.mira_ol}</span>
              <p style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic', fontWeight: 500, fontSize: isMobile?28:38, lineHeight: 1.15, color: '#14120e', margin: 0, alignSelf: 'flex-start' }}>{t.services.mira_kicker}</p>
              <span style={{ display: 'block', width: 80, height: 1, background: 'var(--gold)' }}></span>
              <h2 className="section-title" style={{ fontSize: isMobile?32:48 }}>{t.services.mira_h2}</h2>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24, justifyContent: 'flex-end', paddingTop: isMobile?0:28 }}>
              <p className="body-text">{t.services.mira_body}</p>
              <div style={{ background: 'var(--navy-dark)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', padding: '20px 28px', borderLeft: '3px solid var(--gold)', marginTop: 4 }}>
                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', lineHeight: 1.6, fontStyle: 'italic' }}>{t.services.mira_quote}</p>
              </div>
            </div>
          </div>
          {/* Three trust pillars — compliance, traceability, access */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile?'minmax(0,1fr)':'repeat(3, 1fr)', gap: isMobile?12:2 }}>
            {t.services.mira_apps.map((a, i) =>
            <div key={i} style={{ background: '#ffffff', border: '1px solid var(--cream-dark)', borderTop: '2px solid var(--gold)', padding: '32px 28px 36px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--gold)', fontWeight: 500, fontFamily: "'DM Sans', sans-serif" }}>{a.tag}</div>
                <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: isMobile?22:24, fontWeight: 500, color: 'var(--navy-dark)', lineHeight: 1.25 }}>{a.label}</h3>
                <div style={{ width: 28, height: 1, background: 'var(--gold)' }}></div>
                <p style={{ fontSize: 13, lineHeight: 1.65, color: 'var(--text-muted)', fontWeight: 300 }}>{a.desc}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: 'var(--navy-dark)', padding: isMobile?'40px 20px':'80px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24, textAlign: 'center' }}>
        <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, color: 'rgba(255,255,255,0.6)', fontStyle: 'italic' }}>{t.services.cta_quote}</p>
        <button className="btn-primary" onClick={() => setPage('contact')}>{t.services.cta}</button>
      </section>
    </div>);

}

/* ══════════════════════════════
   PAGE ABOUT
══════════════════════════════ */
function PageAbout({ setPage, lang }) {
  const t = T[lang];
  const isMobile = useIsMobile();
  const revealRef = React.useRef(null);
  useGSAPReveal(revealRef, 0.9);
  const pageRef = React.useRef(null);
  useScrollReveal(pageRef);
  const heroImgRef = React.useRef(null);
  useImageReveal(heroImgRef);
  return (
    <div ref={pageRef} className="page">
      {/* HERO */}
      <section style={{ minHeight: '100svh', background: '#1A1510', position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'flex-end', paddingTop: isMobile?70:120 }}>
        {/* Full-bleed background video */}
        <div ref={heroImgRef} style={{ position: 'absolute', inset: 0 }}>
          <video autoPlay={!introRunning()} muted loop playsInline preload="auto" poster="Images/poster_about_hero.jpg" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }}>
            <source src="Images/video_about_hero.mp4" type="video/mp4" />
          </video>
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(26,21,16,0.30) 0%, rgba(26,21,16,0.40) 40%, rgba(26,21,16,0.92) 100%)' }}></div>
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(26,21,16,0.85) 0%, rgba(26,21,16,0.35) 45%, rgba(26,21,16,0) 70%)' }}></div>
        </div>

        {/* Text, sitting directly on the video */}
        <div style={{ position: 'relative', zIndex: 1, padding: isMobile?'0 20px 56px':'0 80px 100px', maxWidth: isMobile?'100%':620, display: 'flex', flexDirection: 'column', gap: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <span className="overline">{t.about.ol}</span>
          </div>
          <div ref={revealRef}>
            <div className="line-mask"><h1 className="reveal-text" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: isMobile?36:60, fontWeight: 400, lineHeight: 1.08, color: '#fff', margin: 0 }}>{t.about.h1a}</h1></div>
            <div className="line-mask"><div className="reveal-text nth2" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: isMobile?36:60, fontWeight: 400, lineHeight: 1.08, color: '#fff', margin: 0 }}>{t.about.h1b}<em style={{ color: 'var(--gold)' }}> {t.about.h1em}</em></div></div>
          </div>
          <p className="body-text-light" style={{ maxWidth: 460 }}>{t.about.body}</p>
        </div>
      </section>

      {/* FONDATEUR */}
      <section style={{ background: 'var(--cream)', padding: isMobile?'40px 20px':'120px 80px' }}>
        <div style={{ maxWidth: 1300, margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile?'minmax(0,1fr)':'1fr 1fr', gap: isMobile?40:80, alignItems: 'start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <span className="overline">{t.about.found_ol}</span>
            <span className="gold-rule-long"></span>
            <img src="Images/img_50.jpg" alt="L'approche de SynRegis en gestion de copropriété à l'Île Maurice" style={{ width: '100%', height: isMobile?220:360, objectFit: 'cover', objectPosition: 'center bottom', display: 'block', marginTop: 8 }} />
            <div className="glass-dark" style={{ padding: '28px 28px', borderLeft: '3px solid var(--gold)', marginTop: 8 }}>
              <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, fontStyle: 'italic', color: 'rgba(255,255,255,0.8)', lineHeight: 1.5 }}>{t.about.found_quote}</p>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
            <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, lineHeight: 1.5, color: 'var(--navy)', fontStyle: 'italic' }}>{t.about.found_h2}</p>
            <div style={{ height: 1, background: 'var(--cream-dark)' }}></div>
            <p className="body-text">{t.about.found_p1} <span style={{ fontWeight: 500, textDecoration: 'underline', textDecorationColor: 'var(--gold)', textDecorationThickness: 2, textUnderlineOffset: 5 }}>{t.about.found_p1em}</span></p>
          </div>
        </div>
      </section>

      {/* POURQUOI SYNREGIS EXISTE */}
      <section style={{ background: 'var(--navy)', padding: isMobile?'40px 20px':'120px 80px' }}>
        <div style={{ maxWidth: 1300, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile?'minmax(0,1fr)':'1fr 1.5fr', gap: isMobile?40:80, alignItems: 'start' }}>
            <div>
              <span className="overline">{t.about.gen_ol}</span>
              <span className="gold-rule-long" style={{ marginTop: 16, marginBottom: 28, display: 'block' }}></span>
              <h2 className="section-title-light" style={{ fontSize: isMobile?28:48 }}>{t.about.gen_h2}</h2>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              <p className="body-text-light">{t.about.gen_p1}</p>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile?'minmax(0,1fr)':'1fr 1fr', gap: isMobile?12:16 }}>
                {t.about.gen_problems.map((p) =>
                <div key={p} style={{ padding: '16px 20px', background: 'rgba(255,255,255,0.04)', borderLeft: '2px solid rgba(196,160,82,0.4)', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 10, color: 'var(--gold)' }}>✕</span>
                    <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', fontWeight: 300 }}>{p}</span>
                  </div>
                )}
              </div>
              <p className="body-text-light">{t.about.gen_p2}</p>
            </div>
          </div>
        </div>
      </section>

      {/* DIFFÉRENCIATION */}
      <section style={{ background: 'var(--cream)', padding: isMobile?'40px 20px':'120px 80px' }}>
        <div style={{ maxWidth: 1300, margin: '0 auto' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: isMobile?40:72 }}>
            <span className="overline">{t.about.diff_ol}</span>
            <span className="gold-rule-long"></span>
            <h2 className="section-title" style={{ fontSize: isMobile?28:52 }}>{t.about.diff_h2}</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile?'minmax(0,1fr)':'repeat(3, 1fr)', gap: 2 }}>
            {t.about.diff_items.
            map((d, i) =>
            <div key={i} style={{ background: 'rgba(255,255,255,0.65)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.4)', overflow: 'hidden' }}>
                <img src={d.img} alt={d.title} style={{ width: '100%', height: 220, objectFit: 'cover', objectPosition: d.pos || 'center center', display: 'block' }} />
                <div style={{ padding: '32px 28px 36px' }}>
                  <div style={{ width: 30, height: 1, background: 'var(--gold)', marginBottom: 20 }}></div>
                  <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, fontWeight: 500, color: 'var(--navy-dark)', lineHeight: 1.2, marginBottom: 14 }}>{d.title}</h3>
                  <p style={{ fontSize: 13, lineHeight: 1.65, color: 'var(--text-muted)', fontWeight: 300 }}>{d.body}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: 'var(--navy-dark)', padding: isMobile?'40px 20px':'100px 80px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24, textAlign: 'center' }}>
        <h2 className="section-title-light" style={{ fontSize: isMobile?28:44 }}>{t.about.cta_h2}</h2>
        <p className="body-text-light" style={{ maxWidth: 480 }}>
          {t.about.cta_body}
        </p>
        <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button className="btn-primary" onClick={() => setPage('contact')}>{t.about.cta1}</button>
          <button className="btn-ghost" onClick={() => setPage('services')}>{t.about.cta2}</button>
        </div>
      </section>
    </div>);

}

/* ══════════════════════════════
   PAGE CONTACT
══════════════════════════════ */
function PageContact({ lang }) {
  const t = T[lang] || T['fr'];
  const isMobile = useIsMobile();
  const revealRef = React.useRef(null);
  useGSAPReveal(revealRef, 0.9);
  const pageRef = React.useRef(null);
  useScrollReveal(pageRef);
  const [form, setForm] = useState({ nom: '', societe: '', tel: '', email: '', message: '' });
  const [sent, setSent] = useState(false);
  const [active, setActive] = useState(null);

  return (
    <div ref={pageRef} className="page">
      {/* HERO */}
      <section id="contact-info" style={{ background: 'var(--navy-dark)', padding: isMobile?'70px 20px 40px':'180px 80px 100px', borderBottom: '1px solid rgba(196,160,82,0.15)' }}>
        <div style={{ maxWidth: 1300, margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile?'minmax(0,1fr)':'1fr 1fr', gap: isMobile?40:80, alignItems: 'end' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <span className="overline">{t.contact.ol}</span>
            </div>
            <div ref={revealRef}>
              <div className="line-mask"><h1 className="reveal-text" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: isMobile?44:72, fontWeight: 400, lineHeight: 1.1, color: '#fff', margin: 0 }}>{t.contact.h1a}</h1></div>
              <div className="line-mask"><div className="reveal-text nth2" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: isMobile?44:72, fontWeight: 400, lineHeight: 1.1, color: '#fff', margin: 0 }}><em style={{ color: 'var(--gold)' }}>{t.contact.h1b}</em></div></div>
            </div>
            <p className="body-text-light" style={{ maxWidth: 420 }}>
              {t.contact.body}
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingBottom: 8 }}>
            {[
            { label: t.contact.lbl_email, val: 'contact@synregis.com', href: 'mailto:contact@synregis.com' },
            { label: t.contact.lbl_tel,   val: '+230 59498680',        href: 'tel:+23059498680' },
            { label: t.contact.lbl_site,  val: 'synregis.com',         href: null },
            { label: t.contact.lbl_loc,   val: 'Albion, Île Maurice',  href: null }].
            map((c) => {
              const href = c.href;
              return (
                <div key={c.label} style={{ display: 'flex', gap: 24, padding: '14px 0', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                  <span style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--gold)', width: 100, fontFamily: 'DM Sans', flexShrink: 0 }}>{c.label}</span>
                  {href
                    ? <a href={href} className="contact-link">{c.val}</a>
                    : <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.7)', fontWeight: 300 }}>{c.val}</span>
                  }
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 3 PORTES */}
      <section style={{ background: 'var(--cream)', padding: isMobile?'40px 20px':'100px 80px' }}>
        <div style={{ maxWidth: 1300, margin: '0 auto' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: isMobile?30:60 }}>
            <span className="overline">{t.contact.how_ol}</span>
            <span className="gold-rule-long"></span>
            <h2 className="section-title" style={{ fontSize: isMobile?28:44 }}>{t.contact.how_h2}</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile?'minmax(0,1fr)':'repeat(3, 1fr)', gap: 2 }}>
            {t.contact.doors.
            map((p, i) =>
            <div key={i} style={{ background: 'rgba(255,255,255,0.68)', backdropFilter: 'blur(18px)', WebkitBackdropFilter: 'blur(18px)', border: '1px solid rgba(255,255,255,0.45)', overflow: 'hidden', cursor: 'pointer', transition: 'all 0.2s', outline: active === i ? `2px solid var(--gold)` : '2px solid transparent' }} onClick={() => { setActive(i); document.getElementById('contact-info')?.scrollIntoView({ behavior: 'smooth' }); }}>
                <img src={p.img} alt={p.title} style={{ width: '100%', height: isMobile?140:200, objectFit: 'cover', display: 'block', objectPosition: 'center center' }} />
                <div style={{ padding: '32px 28px 40px' }}>
                  <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 14, color: 'var(--gold)', letterSpacing: '0.2em', marginBottom: 16 }}>{p.num}</div>
                  <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 26, fontWeight: 500, color: 'var(--navy-dark)', lineHeight: 1.2, marginBottom: 14 }}>{p.title}</h3>
                  <p style={{ fontSize: 13, lineHeight: 1.65, color: 'var(--text-muted)', fontWeight: 300, marginBottom: 24 }}>{p.body}</p>
                  <button style={{ background: 'none', border: 'none', color: 'var(--gold)', fontSize: 11, letterSpacing: '0.15em', textTransform: 'uppercase', fontFamily: 'DM Sans', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500, padding: 0 }}>
                    {p.cta} <span>→</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FORMULAIRE */}
      <section style={{ background: 'var(--navy-dark)', padding: isMobile?'40px 20px':'100px 80px' }}>
        <div style={{ maxWidth: 1300, margin: '0 auto', display: 'flex', justifyContent: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile?16:24, maxWidth: 620, width: '100%', alignItems: 'center', textAlign: 'center' }}>
            <img src="Images/img_57.png" alt="SynRegis" style={{ height: isMobile?60:100, objectFit: 'contain', objectPosition: 'left', opacity: 0.95, marginBottom: 4 }} />
            <h2 className="section-title-light" style={{ fontSize: isMobile?26:44 }}>{t.contact.form_h2}</h2>
            <p className="body-text-light">{t.contact.form_body}</p>
            <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, fontStyle: 'italic', color: 'rgba(255,255,255,0.4)', lineHeight: 1.6 }}>
                {t.contact.form_quote}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ background: 'var(--navy-dark)', padding: isMobile?'28px 20px':'48px 80px', borderTop: '1px solid rgba(196,160,82,0.12)', display: 'flex', justifyContent: isMobile?'center':'space-between', alignItems: 'center', flexWrap: 'wrap', gap: isMobile?16:24, flexDirection: isMobile?'column':'row' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <img src="Images/img_58.png" alt="SynRegis" style={{ height: 44, opacity: 0.5 }} />
          <div style={{ height: 30, width: 1, background: 'rgba(255,255,255,0.1)' }}></div>
          <div>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 15, color: 'rgba(255,255,255,0.6)', letterSpacing: '0.1em' }}>{t.contact.footer_tag}</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', marginTop: 4 }}>Île Maurice · synregis.com</div>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: isMobile?'center':'flex-end', gap: 12 }}>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 15, fontStyle: 'italic', color: 'rgba(255,255,255,0.3)', maxWidth: 380, textAlign: isMobile?'center':'right', lineHeight: 1.5 }}>
            {t.contact.footer_quote}
          </div>
          <div style={{ display: 'flex', gap: 18 }}>
            <a href="https://www.instagram.com/synregis1/" target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)', textDecoration: 'none' }}>Instagram</a>
            <a href="https://www.linkedin.com/company/135563952/" target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)', textDecoration: 'none' }}>LinkedIn</a>
            <a href="https://share.google/iVYmvHVoz0Nzo5Rat" target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)', textDecoration: 'none' }}>Google</a>
          </div>
        </div>
      </footer>
    </div>);

}

/* ══════════════════════════════
   PAGE FAQ
══════════════════════════════ */
function PageFAQ({ setPage, lang }) {
  const t = T[lang];
  const isMobile = useIsMobile();
  const revealRef = React.useRef(null);
  useGSAPReveal(revealRef, 0.9);
  const pageRef = React.useRef(null);
  useScrollReveal(pageRef);
  const [openIdx, setOpenIdx] = React.useState(0);
  const heroImgRef = React.useRef(null);
  useImageReveal(heroImgRef);
  return (
    <div ref={pageRef} className="page">
      {/* HERO — 2 cols: text left, document/pen image right */}
      <section style={{ minHeight: isMobile?'auto':'70vh', background: 'var(--navy-dark)', display: 'grid', gridTemplateColumns: isMobile?'minmax(0,1fr)':'1.05fr 1fr', alignItems: 'stretch', paddingTop: isMobile?70:120, position: 'relative', overflow: 'hidden' }}>
        {/* Left: text */}
        <div style={{ position: 'relative', zIndex: 1, padding: isMobile?'40px 20px':'80px 64px 80px 80px', display: 'flex', flexDirection: 'column', justifyContent: 'center', maxWidth: '100%' }}>
          {/* subtle decorative circle behind text */}
          <div style={{ position: 'absolute', left: isMobile?-100:-60, top: isMobile?-100:-60, width: isMobile?260:420, height: isMobile?260:420, borderRadius: '50%', border: '1px solid rgba(196,160,82,0.07)', pointerEvents: 'none' }}></div>
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28 }}>
              <span className="gold-rule-long"></span>
              <span className="overline">{t.faq.ol}</span>
            </div>
            <div ref={revealRef}>
              <div className="line-mask"><h1 className="reveal-text" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: isMobile?40:60, fontWeight: 400, lineHeight: 1.1, color: '#fff', margin: 0 }}>{t.faq.h1a}</h1></div>
              <div className="line-mask"><div className="reveal-text nth2" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: isMobile?40:60, fontWeight: 400, lineHeight: 1.1, color: '#fff', margin: 0 }}>{t.faq.h1b}</div></div>
              <div className="line-mask" style={{ marginBottom: 28 }}><div className="reveal-text nth3" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: isMobile?40:60, fontWeight: 400, lineHeight: 1.1, color: '#fff', margin: 0 }}><em style={{ color: 'var(--gold)' }}>{t.faq.h1em}</em></div></div>
            </div>
            <p className="body-text-light" style={{ maxWidth: 520 }}>{t.faq.body}</p>
          </div>
        </div>
        {/* Right: editorial photo */}
        <div ref={heroImgRef} style={{ position: 'relative', minHeight: isMobile?320:'auto', overflow: 'hidden' }}>
          <img src="Images/faq.jpg" alt={lang === 'fr' ? 'Document de copropriété et stylo plume' : 'Co-ownership document and fountain pen'} style={{ width: '100%', height: '100%', minHeight: isMobile?320:'auto', objectFit: 'cover', display: 'block' }} />
          {/* Subtle gradient to bond image with the navy hero on the left edge */}
          <div style={{ position: 'absolute', inset: 0, background: isMobile?'linear-gradient(180deg, rgba(26,21,16,0.0) 60%, rgba(26,21,16,0.45) 100%)':'linear-gradient(90deg, rgba(26,21,16,0.45) 0%, rgba(26,21,16,0) 25%)', pointerEvents: 'none' }}></div>
          {/* Gold corner accents — same motif as homepage hero */}
          {!isMobile && (
            <>
              <div style={{ position: 'absolute', top: 32, right: 32, width: 64, height: 64, borderTop: '2px solid var(--gold)', borderRight: '2px solid var(--gold)', opacity: 0.5 }}></div>
              <div style={{ position: 'absolute', bottom: 32, right: 32, width: 64, height: 64, borderBottom: '2px solid var(--gold)', borderRight: '2px solid var(--gold)', opacity: 0.5 }}></div>
            </>
          )}
        </div>
      </section>

      {/* INTRO */}
      <section style={{ background: 'var(--cream)', padding: isMobile?'40px 20px':'100px 80px 40px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile?'minmax(0,1fr)':'1fr 1.4fr', gap: isMobile?28:64, alignItems: 'start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <span className="overline">{t.faq.intro_ol}</span>
            <span className="gold-rule-long"></span>
            <h2 className="section-title" style={{ fontSize: isMobile?28:40 }}>{t.faq.intro_h2}</h2>
          </div>
          <p className="body-text" style={{ paddingTop: isMobile?0:12 }}>{t.faq.intro_body}</p>
        </div>
      </section>

      {/* FAQ LIST — accordion */}
      <section style={{ background: 'var(--cream)', padding: isMobile?'12px 20px 40px':'40px 80px 120px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', borderTop: '1px solid var(--cream-dark)' }}>
          {t.faq.items.map((item, i) => {
            const open = openIdx === i;
            return (
              <div key={i} style={{ borderBottom: '1px solid var(--cream-dark)' }}>
                <button
                  onClick={() => setOpenIdx(open ? -1 : i)}
                  aria-expanded={open}
                  style={{
                    width: '100%', background: 'none', border: 'none', cursor: 'pointer',
                    padding: isMobile?'24px 0':'32px 0',
                    display: 'grid', gridTemplateColumns: isMobile?'auto 1fr auto':'56px 1fr auto', gap: isMobile?16:24, alignItems: 'baseline',
                    textAlign: 'left',
                  }}
                >
                  <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: isMobile?14:16, color: 'var(--gold)', letterSpacing: '0.18em', fontWeight: 400 }}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h3 style={{
                    fontFamily: "'Cormorant Garamond', serif", fontWeight: 500,
                    fontSize: isMobile?20:26, lineHeight: 1.3, color: 'var(--navy-dark)', margin: 0,
                  }}>{item.q}</h3>
                  <span style={{
                    fontSize: 22, color: 'var(--gold)', lineHeight: 1, transition: 'transform 0.3s ease',
                    transform: open ? 'rotate(45deg)' : 'rotate(0deg)', display: 'inline-block',
                    fontFamily: "'DM Sans', sans-serif", fontWeight: 300, marginTop: 4,
                  }}>+</span>
                </button>
                <div style={{
                  display: 'grid',
                  gridTemplateRows: open ? '1fr' : '0fr',
                  transition: 'grid-template-rows 0.45s cubic-bezier(0.22, 1, 0.36, 1)',
                }}>
                  <div style={{ overflow: 'hidden' }}>
                    <div style={{ padding: isMobile?'0 0 24px 0':'0 80px 36px 80px' }}>
                      <p className="body-text" style={{ fontSize: isMobile?14:15.5 }}>{item.a}</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA FINAL — mirrors the homepage closing block, but only ONE button */}
      <section style={{ background: 'var(--navy-dark)', padding: isMobile?'40px 20px':'120px 80px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', width: isMobile?300:700, height: isMobile?300:700, borderRadius: '50%', border: '1px solid rgba(196,160,82,0.06)', pointerEvents: 'none' }}></div>
        <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%,-50%)', width: isMobile?200:500, height: isMobile?200:500, borderRadius: '50%', border: '1px solid rgba(196,160,82,0.08)', pointerEvents: 'none' }}></div>
        <div style={{ position: 'relative', zIndex: 1, maxWidth: 700, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 24, alignItems: 'center' }}>
          <img src="Images/img_43.png" alt="SynRegis" style={{ height: isMobile?70:130, opacity: 0.95, marginBottom: isMobile?8:16 }} />
          <h2 className="section-title-light" style={{ fontSize: isMobile?32:52 }}>{t.faq.cta_h2}</h2>
          <p className="body-text-light" style={{ maxWidth: 480 }}>{t.faq.cta_body}</p>
          <div style={{ display: 'flex', gap: 16, marginTop: 16, justifyContent: 'center' }}>
            <button className="btn-primary" onClick={() => setPage('contact')}>{t.faq.cta1}</button>
          </div>
        </div>
      </section>
    </div>);

}

/* ══════════════════════════════
   TWEAKS
══════════════════════════════ */
const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
  "colorAccent": "#d5ba81",
  "fontDisplay": "Cormorant Garamond",
  "navyDark": "#a47d56",
  "heroSize": 76
}/*EDITMODE-END*/;

function TweaksPanel({ tweaks, setTweaks, visible }) {
  if (!visible) return null;
  return (
    <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1000, background: '#1a2235', border: '1px solid rgba(196,160,82,0.3)', padding: '20px 24px', minWidth: 260, boxShadow: '0 8px 40px rgba(0,0,0,0.5)' }}>
      <div style={{ fontSize: 11, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: 16, fontFamily: 'DM Sans' }}>Tweaks</div>
      {[
      { key: 'colorAccent', label: "Couleur Or", type: 'color' },
      { key: 'navyDark', label: "Fond sombre", type: 'color' },
      { key: 'heroSize', label: "Taille hero (px)", type: 'range', min: 48, max: 96, step: 4 }].
      map((t) =>
      <div key={t.key} style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'space-between' }}>
          <label style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', fontFamily: 'DM Sans', flexShrink: 0 }}>{t.label}</label>
          {t.type === 'color' &&
        <input type="color" value={tweaks[t.key]} onChange={(e) => setTweaks({ ...tweaks, [t.key]: e.target.value })} style={{ width: 40, height: 24, border: 'none', background: 'none', cursor: 'pointer' }} />
        }
          {t.type === 'range' &&
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="range" min={t.min} max={t.max} step={t.step} value={tweaks[t.key]} onChange={(e) => setTweaks({ ...tweaks, [t.key]: Number(e.target.value) })} style={{ width: 100 }} />
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', width: 28, textAlign: 'right', fontFamily: 'DM Sans' }}>{tweaks[t.key]}</span>
            </div>
        }
        </div>
      )}
    </div>);

}

/* ══════════════════════════════
   LEGAL — slim site-wide copyright strip + a modal holding the bilingual
   "Mentions légales" (legal notice) and "Politique de confidentialité"
   (privacy policy, Mauritius Data Protection Act 2017).
══════════════════════════════ */
function LegalBar({ lang, onOpen }) {
  const isMobile = useIsMobile();
  const fr = lang === 'fr';
  return (
    <div style={{ background: '#14120e', padding: isMobile ? '6px 20px' : '6px 64px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, flexWrap: 'wrap', lineHeight: 1.3 }}>
      <span style={{ fontSize: 9.5, color: 'rgba(255,255,255,0.35)', fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.02em' }}>
        © 2026 SynRegis – {fr ? 'Tous droits réservés.' : 'All rights reserved.'}
      </span>
      <span style={{ color: 'rgba(255,255,255,0.18)', fontSize: 9.5 }}>·</span>
      <button onClick={onOpen} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 9.5, color: 'rgba(255,255,255,0.5)', fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.02em', textDecoration: 'underline', textUnderlineOffset: 2, padding: 0 }}>
        {fr ? 'Mentions légales' : 'Legal notice'}
      </button>
    </div>
  );
}

function LegalModal({ lang, onClose }) {
  const fr = lang === 'fr';
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, []);

  const items = fr ? [
    { s: 'Mentions légales' },
    { h: 'Éditeur du site', p: "Le présent site est édité par SynRegis Ltd, dont le siège social est situé à Albion, Île Maurice. Numéro d'enregistrement des sociétés (BRN) : C26232100. Courriel : contact@synregis.com – Téléphone : +230 5949 8680. Directeur de la publication : Axel Noyan." },
    { h: 'Hébergement', p: "Ce site est hébergé par Vercel Inc., 340 S Lemon Avenue #4133, Walnut, CA 91789, États-Unis." },
    { h: 'Propriété intellectuelle', p: "L'ensemble des contenus de ce site (textes, logo « SynRegis », images et mise en page) est protégé et demeure la propriété exclusive de SynRegis. Toute reproduction, représentation ou diffusion, totale ou partielle, sans autorisation écrite préalable, est interdite." },
    { s: 'Politique de confidentialité' },
    { h: 'Responsable du traitement', p: "SynRegis (coordonnées ci-dessus) est responsable du traitement des données personnelles collectées via ce site, conformément au Data Protection Act 2017 de l'Île Maurice." },
    { h: 'Données collectées et finalité', p: "Les seules données personnelles traitées sont celles que vous nous communiquez volontairement (nom, adresse e-mail, numéro de téléphone, contenu de votre message) lorsque vous nous contactez. Elles servent exclusivement à répondre à votre demande et à vous présenter nos services. Aucune donnée n'est vendue ni transmise à des tiers à des fins commerciales." },
    { h: 'Mesure d’audience et cookies', p: "Ce site n'utilise aucun cookie de suivi publicitaire. La mesure d'audience est anonyme et agrégée, sans identification personnelle." },
    { h: 'Conservation des données', p: "Vos données sont conservées uniquement le temps nécessaire au traitement de votre demande, puis supprimées ou archivées conformément à la loi mauricienne." },
    { h: 'Vos droits', p: "Conformément au Data Protection Act 2017, vous disposez d'un droit d'accès, de rectification, d'effacement et d'opposition concernant vos données personnelles. Pour exercer ces droits, écrivez à contact@synregis.com." },
    { note: 'Dernière mise à jour : juin 2026.' },
  ] : [
    { s: 'Legal notice' },
    { h: 'Site publisher', p: "This website is published by SynRegis Ltd, with its registered office in Albion, Mauritius. Business Registration Number (BRN): C26232100. Email: contact@synregis.com – Phone: +230 5949 8680. Publication director: Axel Noyan." },
    { h: 'Hosting', p: "This website is hosted by Vercel Inc., 340 S Lemon Avenue #4133, Walnut, CA 91789, USA." },
    { h: 'Intellectual property', p: "All content on this site (text, the “SynRegis” logo, images and layout) is protected and remains the exclusive property of SynRegis. Any reproduction, representation or distribution, in whole or in part, without prior written authorisation is prohibited." },
    { s: 'Privacy policy' },
    { h: 'Data controller', p: "SynRegis (contact details above) is the controller of the personal data collected through this website, in accordance with the Mauritius Data Protection Act 2017." },
    { h: 'Data collected and purpose', p: "The only personal data processed is what you voluntarily provide (name, email address, phone number, message content) when you contact us. It is used solely to respond to your request and present our services. No data is sold or shared with third parties for commercial purposes." },
    { h: 'Analytics and cookies', p: "This site uses no advertising or tracking cookies. Audience measurement is anonymous and aggregated, with no personal identification." },
    { h: 'Data retention', p: "Your data is kept only for as long as necessary to handle your request, then deleted or archived in accordance with Mauritian law." },
    { h: 'Your rights', p: "Under the Data Protection Act 2017, you have the right to access, rectify, erase and object to the processing of your personal data. To exercise these rights, email contact@synregis.com." },
    { note: 'Last updated: June 2026.' },
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'var(--cream)', overflowY: 'auto' }}>
      {/* Sticky header in the site's theme — logo + close */}
      <div style={{ position: 'sticky', top: 0, zIndex: 2, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 clamp(20px, 5vw, 64px)', background: 'rgba(250,248,244,0.96)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', borderBottom: '1px solid rgba(196,160,82,0.2)' }}>
        <img src="Images/synregis_mark.png" alt="SynRegis" style={{ height: 34, objectFit: 'contain' }} />
        <button onClick={onClose} style={{ background: 'none', border: '1px solid rgba(196,160,82,0.55)', color: 'var(--navy-dark)', cursor: 'pointer', fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: "'DM Sans', sans-serif", padding: '9px 22px' }}>{fr ? 'Fermer' : 'Close'}</button>
      </div>

      <div style={{ maxWidth: 820, margin: '0 auto', padding: 'clamp(48px, 7vw, 88px) clamp(20px, 5vw, 40px) 96px' }}>
        <span className="overline">{fr ? 'Informations légales' : 'Legal information'}</span>
        <span className="gold-rule-long" style={{ marginTop: 18 }}></span>
        {items.map((it, i) => {
          if (it.s) return <h2 key={i} style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(30px, 4vw, 40px)', fontWeight: 500, color: 'var(--navy-dark)', lineHeight: 1.1, margin: i === 0 ? '20px 0 8px' : '52px 0 8px' }}>{it.s}</h2>;
          if (it.note) return <p key={i} style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic', marginTop: 32 }}>{it.note}</p>;
          return (
            <div key={i}>
              <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 500, color: 'var(--navy-dark)', margin: '26px 0 8px' }}>{it.h}</h3>
              <p className="body-text" style={{ margin: 0 }}>{it.p}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ══════════════════════════════
   APP
══════════════════════════════ */
function BackgroundMusic({ lang }) {
  // The audio element is created (and started) by the inline gate script in index.html
  // so the music can begin before the intro; here we only expose the mute toggle.
  const [muted, setMuted] = useState(() => { try { return localStorage.getItem('synregis_music') === 'off'; } catch (e) { return false; } });
  const TARGET = 0.35;
  const toggle = () => {
    const a = window.__srMusic || (window.__srMusic = Object.assign(new Audio('/Images/tunetank-inspiring-cinematic-music-409347.mp3'), { loop: true }));
    const next = !muted;
    setMuted(next);
    try { localStorage.setItem('synregis_music', next ? 'off' : 'on'); } catch (e) {}
    if (!a) return;
    if (next) a.pause();
    else { a.volume = TARGET; a.play().catch(() => {}); }
  };
  const label = lang === 'fr' ? (muted ? 'Activer la musique' : 'Couper la musique') : (muted ? 'Play music' : 'Mute music');
  return (
    <>
      <button onClick={toggle} aria-label={label} title={label} style={{ position: 'fixed', left: 20, bottom: 40, zIndex: 900, width: 40, height: 40, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.35)', background: 'rgba(20,18,14,0.55)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M11 5 6 9H3v6h3l5 4V5z" fill="#fff" />
          {muted ? <><line x1="16" y1="9" x2="22" y2="15" /><line x1="22" y1="9" x2="16" y2="15" /></> : <><path d="M15.5 8.5a5 5 0 0 1 0 7" /><path d="M18.5 5.5a9 9 0 0 1 0 13" /></>}
        </svg>
      </button>
    </>
  );
}

function App() {
  const getInitialPage = () => {
    const path = window.location.pathname.replace(/^\//, '').replace(/\/$/, '').replace(/\.html$/, '') || 'home';
    return ['home', 'services', 'about', 'contact', 'faq'].includes(path) ? path : 'home';
  };
  const [page, setPage] = useState(getInitialPage);
  const [tweaks, setTweaks] = useState(TWEAK_DEFAULTS);
  const [tweaksVisible, setTweaksVisible] = useState(false);
  const [legalOpen, setLegalOpen] = useState(false);
  const detectLang = () => {
    // Priority 1: explicit ?lang= in the URL — this is the per-language URL that
    // Google indexes (clean = FR, ?lang=en = EN), so it must win.
    try {
      const p = new URLSearchParams(window.location.search).get('lang');
      if (p === 'fr' || p === 'en') return p;
    } catch (e) {}
    // Priority 2: a previously chosen preference.
    const saved = localStorage.getItem('synregis_lang');
    if (saved === 'fr' || saved === 'en') return saved;
    // Priority 3: default to French. The site is French-first (Mauritius) and the
    // clean URL IS the French version. We deliberately do NOT auto-switch to
    // English by browser language — Googlebot crawls as en, and that would make
    // it index English content on the clean (French) URL.
    return 'fr';
  };
  const [lang, setLangState] = useState(detectLang);
  const setLang = (l) => {
    setLangState(l);
    try { localStorage.setItem('synregis_lang', l); } catch (e) {}
    // Reflect the choice in the URL so the EN view has its own shareable,
    // crawlable URL (?lang=en) whose canonical matches it; FR strips the param.
    try {
      const url = new URL(window.location.href);
      if (l === 'en') url.searchParams.set('lang', 'en');
      else url.searchParams.delete('lang');
      window.history.replaceState(null, '', url.pathname + url.search);
    } catch (e) {}
  };

  const navigateTo = (newPage) => {
    if (newPage === page) return;
    const base = newPage === 'home' ? '/' : `/${newPage}`;
    // Carry the active language across navigation so an EN visitor keeps browsing
    // the ?lang=en URLs (and their EN canonicals).
    const slug = base + (lang === 'en' ? '?lang=en' : '');
    window.history.pushState(null, '', slug);
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  useEffect(() => {
    document.documentElement.style.setProperty('--gold', tweaks.colorAccent);
    document.documentElement.style.setProperty('--navy-dark', tweaks.navyDark);
    document.documentElement.style.setProperty('--navy', tweaks.navyDark);
    window.parent.postMessage({ type: '__edit_mode_set_keys', edits: tweaks }, '*');
  }, [tweaks]);

  // Always open at the top of the page on (re)load so the hero + intro animations
  // play from the start — never restore a mid-page scroll position.
  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleMsg = (e) => {
      if (e.data?.type === '__activate_edit_mode') setTweaksVisible(true);
      if (e.data?.type === '__deactivate_edit_mode') setTweaksVisible(false);
    };
    window.addEventListener('message', handleMsg);
    window.parent.postMessage({ type: '__edit_mode_available' }, '*');
    return () => window.removeEventListener('message', handleMsg);
  }, []);

  // Sync page on browser back/forward
  useEffect(() => {
    const handlePop = () => {
      const path = window.location.pathname.replace(/^\//, '').replace(/\/$/, '').replace(/\.html$/, '') || 'home';
      const p = ['home', 'services', 'about', 'contact', 'faq'].includes(path) ? path : 'home';
      setPage(p);
      try {
        const ql = new URLSearchParams(window.location.search).get('lang');
        setLangState(ql === 'en' ? 'en' : 'fr');
      } catch (e) {}
      window.scrollTo({ top: 0, behavior: 'instant' });
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, []);

  // Update meta tags + canonical URL on page change (bilingual)
  useEffect(() => {
    const metaMap = {
      fr: {
        home: {
          title: 'SynRegis – Syndic de Copropriété | Île Maurice',
          desc: "SynRegis est le syndic de copropriété professionnel à l'Île Maurice. Gestion rigoureuse, maîtrise juridique et reporting transparent pour les promoteurs immobiliers.",
          url: 'https://synregis.com/'
        },
        services: {
          title: "Services – Gestion de Copropriété à l'Île Maurice | SynRegis",
          desc: "Gestion courante, reporting financier, assemblées générales et communication traçable. Les prestations SynRegis pour promoteurs exigeants à l'Île Maurice.",
          url: 'https://synregis.com/services'
        },
        about: {
          title: "À propos – SynRegis, Syndic de Copropriété à l'Île Maurice",
          desc: "SynRegis a été fondé pour répondre au manque de rigueur juridique dans la gestion de copropriété à Maurice. Une expertise unique, un seul marché.",
          url: 'https://synregis.com/about'
        },
        contact: {
          title: "Contact – SynRegis, Syndic à l'Île Maurice",
          desc: "Contactez SynRegis pour la gestion de votre copropriété à l'Île Maurice. Syndic expert, rigoureux et réactif pour promoteurs immobiliers.",
          url: 'https://synregis.com/contact'
        },
        faq: {
          title: "FAQ – Syndic de Copropriété à l'Île Maurice | SynRegis",
          desc: "Questions fréquentes sur le métier de syndic à l'Île Maurice : choisir, changer de syndic, VEFA, AG, charges, plateforme digitale. Réponses claires par SynRegis.",
          url: 'https://synregis.com/faq'
        }
      },
      en: {
        home: {
          title: 'SynRegis – Property Management & Syndic in Mauritius',
          desc: "SynRegis is the professional property management firm in Mauritius. Rigorous management, legal expertise and transparent reporting for real estate developers.",
          url: 'https://synregis.com/'
        },
        services: {
          title: "Services – Property Management in Mauritius | SynRegis",
          desc: "Day-to-day management, financial reporting, general meetings and traceable communication. SynRegis services for demanding developers in Mauritius.",
          url: 'https://synregis.com/services'
        },
        about: {
          title: "About – SynRegis, Property Management in Mauritius",
          desc: "SynRegis was founded to address the lack of legal rigor in Mauritian property management. A single market, a single expertise.",
          url: 'https://synregis.com/about'
        },
        contact: {
          title: "Contact – SynRegis, Mauritius Syndic & Property Manager",
          desc: "Contact SynRegis for the management of your co-ownership in Mauritius. Expert, rigorous and responsive property manager for real estate developers.",
          url: 'https://synregis.com/contact'
        },
        faq: {
          title: "FAQ – Property Management & Syndic in Mauritius | SynRegis",
          desc: "Frequently asked questions about property management in Mauritius: choosing or changing a syndic, off-plan (VEFA), AGMs, service charges, digital platform. Clear answers by SynRegis.",
          url: 'https://synregis.com/faq'
        }
      }
    };
    const d = (metaMap[lang] || metaMap.fr)[page] || metaMap.fr.home;
    // Canonical/og:url follow the URL, NOT the displayed language: only the real
    // ?lang=en URL self-canonicals to ?lang=en; the clean URL always
    // self-canonicals to itself (French). This is what lets Google index BOTH
    // languages (clean=FR, ?lang=en=EN) without the clean French URL being
    // demoted to an "alternate" of the English one.
    let urlIsEn = false;
    try { urlIsEn = new URLSearchParams(window.location.search).get('lang') === 'en'; } catch (e) {}
    const canonicalUrl = d.url + (urlIsEn ? '?lang=en' : '');
    document.title = d.title;
    document.documentElement.lang = lang;
    document.querySelector('meta[name="description"]')?.setAttribute('content', d.desc);
    document.querySelector('link[rel="canonical"]')?.setAttribute('href', canonicalUrl);
    document.querySelector('meta[property="og:url"]')?.setAttribute('content', canonicalUrl);
    document.querySelector('meta[property="og:title"]')?.setAttribute('content', d.title);
    document.querySelector('meta[property="og:description"]')?.setAttribute('content', d.desc);
    document.querySelector('meta[property="og:locale"]')?.setAttribute('content', lang === 'en' ? 'en_MU' : 'fr_MU');
    document.querySelector('meta[name="twitter:title"]')?.setAttribute('content', d.title);
    document.querySelector('meta[name="twitter:description"]')?.setAttribute('content', d.desc);
  }, [page, lang]);

  const renderPage = () => {
    switch (page) {
      case 'home':return <PageHome key="home" setPage={navigateTo} lang={lang} />;
      case 'services':return <PageServices key="services" setPage={navigateTo} lang={lang} />;
      case 'about':return <PageAbout key="about" setPage={navigateTo} lang={lang} />;
      case 'contact':return <PageContact key="contact" lang={lang} />;
      case 'faq':return <PageFAQ key="faq" setPage={navigateTo} lang={lang} />;
      default:return <PageHome key="home" setPage={navigateTo} lang={lang} />;
    }
  };

  return (
    <>
      <Nav page={page} setPage={navigateTo} lang={lang} setLang={setLang} />
      {renderPage()}
      <BackgroundMusic lang={lang} />
      <LegalBar lang={lang} onOpen={() => setLegalOpen(true)} />
      {legalOpen && <LegalModal lang={lang} onClose={() => setLegalOpen(false)} />}
      <TweaksPanel tweaks={tweaks} setTweaks={setTweaks} visible={tweaksVisible} />
    </>);

}

ReactDOM.createRoot(document.getElementById('root')).render(<App />); document.getElementById('pg-loader')?.remove();